import { useState, useEffect, useRef } from "react";

/* ─── Design tokens ────────────────────────────────────────────────────────── */
const T = {
  /* Spacing — every value is a multiple of 4 */
  sp2:"2px", sp4:"4px", sp6:"6px", sp8:"8px", sp10:"10px",
  sp12:"12px", sp14:"14px", sp16:"16px", sp20:"20px", sp24:"24px",
  sp28:"28px", sp32:"32px", sp40:"40px", sp48:"48px",
  /* Page padding */
  px: "20px",
  /* Radius */
  r8:"8px", r12:"12px", r14:"14px", r16:"16px", r20:"20px", r24:"24px", r50:"50px",
};

const P = {
  bg:"#F4F2ED", card:"#FFFFFF",
  border:"#EAE5DB", border2:"#D4CDC0",
  text1:"#1A2632", text2:"#5A6A7A", text3:"#98A8B8",
  accent:"#4A8FE7", teal:"#3DB896",
  coral:"#E8705A", amber:"#F0A020", lavender:"#8B7BC8",
  mint:"#E3F6EF", sky:"#E9F3FD", peach:"#FDEEE8", yellow:"#FDF5E2",
  glass:"rgba(248,246,242,0.88)",
  glassB:"rgba(255,255,255,0.95)",
};

/* ─── Data ─────────────────────────────────────────────────────────────────── */
const PRIORITIES = [
  { id:"high",   label:"High",   emoji:"🔴", color:"#D94545", bg:"#FEF0F0" },
  { id:"medium", label:"Medium", emoji:"🟡", color:"#C88010", bg:"#FDF6E8" },
  { id:"low",    label:"Low",    emoji:"🟢", color:"#3DB896", bg:"#E3F6EF" },
];
const ENERGY = [
  { id:"low",    label:"Just one", emoji:"🌱", sub:"Low energy — one is enough",  max:1, c:"#3DB896" },
  { id:"medium", label:"A few",    emoji:"⚡", sub:"Some energy — maybe 2 or 3",  max:3, c:"#4A8FE7" },
  { id:"high",   label:"Bring it", emoji:"🔥", sub:"Firing on all cylinders",       max:5, c:"#E8705A" },
];
const SLOTS = [
  { id:"morning",   label:"Morning",   emoji:"🌅", bg:"#FFF5E2", ac:"#F0A020" },
  { id:"afternoon", label:"Afternoon", emoji:"☀️",  bg:"#E9F3FD", ac:"#4A8FE7" },
  { id:"evening",   label:"Evening",   emoji:"🌙", bg:"#F1EEF9", ac:"#8B7BC8" },
  { id:"anytime",   label:"Any time",  emoji:"✨", bg:"#F4F2ED", ac:"#3DB896"  },
];
const CATS = [
  { id:"task",        label:"Task",        emoji:"✅", color:"#3DB896", act:true  },
  { id:"instruction", label:"Instruction", emoji:"📋", color:"#5A88C0", act:false },
  { id:"advice",      label:"Advice",      emoji:"💡", color:"#E09820", act:false },
  { id:"quote",       label:"Quote",       emoji:"💬", color:"#5A88C0", act:false },
  { id:"affirmation", label:"Affirmation", emoji:"✨", color:"#8B7BC8", act:false },
];
const DURATIONS = [
  {l:"5 min",s:300},{l:"10 min",s:600},{l:"15 min",s:900},
  {l:"25 min",s:1500},{l:"30 min",s:1800},{l:"45 min",s:2700},{l:"60 min",s:3600},
];
const MOODS = [
  {id:"procrastinating",label:"Procrastinating",emoji:"😶‍🌫️"},
  {id:"overwhelmed",label:"Overwhelmed",emoji:"🌊"},
  {id:"anxious",label:"Anxious",emoji:"😬"},
  {id:"stuck",label:"Stuck",emoji:"🧱"},
  {id:"low-energy",label:"Low energy",emoji:"🪫"},
  {id:"giving-up",label:"About to give up",emoji:"🏳️"},
  {id:"distracted",label:"Distracted",emoji:"🌀"},
];
const APPS = ["Instagram","TikTok","YouTube","Twitter / X","Reddit","Netflix","WhatsApp","Snapchat","Facebook","Discord","Twitch","Pinterest"];
const ONBOARD = [
  {e:"💭",h:"You save things\nand forget them.",s:"Tasks, quotes, advice — brilliant things that vanish the moment you close your notes app.",bg:"#FFF5E2"},
  {e:"🎯",h:"Oh Right meets\nyou where you are.",s:"Check in each part of the day. How you feel determines how much you take on.",bg:"#E9F3FD"},
  {e:"🤖",h:"Meet Ripple,\nyour AI.",s:"Chat with Ripple to plan, declutter your brain, and add anything directly — no switching, no friction.",bg:"#F1EEF9"},
];
const SAMPLES = [
  {id:"t1",text:"Reply to the accountant email.",category:"task",actionable:true,priority:"high",contexts:["procrastinating"],timeSlot:"morning",favorite:false,createdAt:Date.now()-864e5*4,doneCount:0,linkedInstructions:["i1"],author:""},
  {id:"t2",text:"Write the first draft of the proposal.",category:"task",actionable:true,priority:"high",contexts:["overwhelmed"],timeSlot:"afternoon",favorite:true,createdAt:Date.now()-864e5*3,doneCount:1,linkedInstructions:["i2","i3"],author:""},
  {id:"t3",text:"Book that appointment you've been putting off.",category:"task",actionable:true,priority:"medium",contexts:["procrastinating"],timeSlot:"anytime",favorite:false,createdAt:Date.now()-864e5*2,doneCount:0,linkedInstructions:[],author:""},
  {id:"t4",text:"Clear the kitchen before bed.",category:"task",actionable:true,priority:"low",contexts:["low-energy"],timeSlot:"evening",favorite:false,createdAt:Date.now()-864e5,doneCount:3,linkedInstructions:["i1"],author:""},
  {id:"i1",text:"Start with just two minutes. Set a timer. That's all.",category:"instruction",actionable:false,priority:"medium",contexts:[],timeSlot:"anytime",favorite:true,createdAt:Date.now()-864e5*5,doneCount:0,linkedInstructions:[],author:""},
  {id:"i2",text:"Write the worst possible first sentence. Done beats perfect.",category:"instruction",actionable:false,priority:"medium",contexts:[],timeSlot:"anytime",favorite:false,createdAt:Date.now()-864e5*4,doneCount:0,linkedInstructions:[],author:""},
  {id:"i3",text:"Name the ONE next physical action — not the whole project.",category:"instruction",actionable:false,priority:"medium",contexts:[],timeSlot:"anytime",favorite:false,createdAt:Date.now()-864e5*3,doneCount:0,linkedInstructions:[],author:""},
  {id:"q1",text:"Done is better than perfect.",category:"quote",actionable:false,priority:"medium",contexts:[],timeSlot:"anytime",favorite:true,createdAt:Date.now()-864e5*6,doneCount:0,linkedInstructions:[],author:"Sheryl Sandberg"},
  {id:"q2",text:"Your brain is not broken. It just needs a different kind of engine.",category:"affirmation",actionable:false,priority:"medium",contexts:[],timeSlot:"anytime",favorite:false,createdAt:Date.now()-864e5*5,doneCount:0,linkedInstructions:[],author:""},
  {id:"q3",text:"You don't have to feel motivated to start. Start first, motivation follows.",category:"advice",actionable:false,priority:"medium",contexts:[],timeSlot:"anytime",favorite:false,createdAt:Date.now()-864e5*4,doneCount:0,linkedInstructions:[],author:""},
];

function localStorageGet(key){return Promise.resolve(localStorage.getItem(key)?{value:localStorage.getItem(key)}:null);}
function localStorageSet(key,value){localStorage.setItem(key,value);return Promise.resolve(true);}
function uid(){return Math.random().toString(36).slice(2)+Date.now().toString(36);}
function todayKey(){const d=new Date();return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;}
function fmt(s){const m=Math.floor(s/60),x=s%60;return `${m}:${x.toString().padStart(2,"0")}`;}
function slot(){const h=new Date().getHours();return h<12?"morning":h<17?"afternoon":"evening";}

/* ─── SVG icons ────────────────────────────────────────────────────────────── */
const Ico = {
  tasks:(on)=><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={on?"#fff":"#98A8B8"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  cal:(on)=><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={on?"#fff":"#98A8B8"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  focus:(on,pct=0,running=false,active=false)=>{
    if(!active){
      return(
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={on?"#fff":"#98A8B8"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
      );
    }
    const C=11, R=8;
    const circ = 2*Math.PI*R;
    const elapsed = Math.min(1, Math.max(0, pct));      // 0→1 as time passes
    const remaining = 1 - elapsed;                       // 1→0 as time passes
    const arcLen = remaining * circ;                     // shrinks over time
    const col    = on?"#fff":"#98A8B8";
    const arcCol = on?"rgba(255,255,255,0.95)":"#3DB896";
    const bgCol  = on?"rgba(255,255,255,0.2)":"#E0DAD0";
    // offset = elapsed*circ → arc ENDS at 12, gap opens anticlockwise from left
    const offset = elapsed * circ;
    return(
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <circle cx={C} cy={C} r={R} stroke={bgCol} strokeWidth="2" fill="none"/>
        <circle cx={C} cy={C} r={R}
          stroke={arcCol} strokeWidth="2.2" fill="none"
          strokeDasharray={`${arcLen} ${circ}`}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${C} ${C})`}
          style={running?{animation:"tp 2s ease-in-out infinite"}:{}}
        />
        <circle cx={C} cy={C} r="2.5" fill={col}/>
      </svg>
    );
  },
  lib:(on)=><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={on?"#fff":"#98A8B8"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  stats:(on)=><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={on?"#fff":"#98A8B8"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
};

/* ─── Circular dial picker ──────────────────────────────────────────────────── */
function DialPicker({ minutes, onChange, sf, ff }) {
  const SIZE   = 290;
  const CX     = SIZE / 2;
  const R      = 114;  // track radius
  const MIN    = 1;
  const MAX    = 60;

  const svgRef = useRef(null);
  const dragging = useRef(false);

  // Clockwise from top, evenly spaced: 1 min just past 12, 60 min = exactly 12
  function minsToAngle(m) {
    return -Math.PI/2 + (m/60) * 2 * Math.PI;
  }
  // Convert pointer → minutes using same even spacing
  function posToMins(clientX, clientY) {
    const rect = svgRef.current.getBoundingClientRect();
    const x = clientX - rect.left - CX;
    const y = clientY - rect.top  - CX;
    let angle = Math.atan2(y, x) + Math.PI/2; // 0 at top
    if (angle < 0) angle += 2 * Math.PI;
    const raw = Math.round((angle / (2 * Math.PI)) * 60);
    return Math.min(MAX, Math.max(MIN, raw === 0 ? 60 : raw));
  }

  function handleDown(e) {
    dragging.current = true;
    const touch = e.touches ? e.touches[0] : e;
    onChange(posToMins(touch.clientX, touch.clientY));
  }
  function handleMove(e) {
    if (!dragging.current) return;
    e.preventDefault();
    const touch = e.touches ? e.touches[0] : e;
    onChange(posToMins(touch.clientX, touch.clientY));
  }
  function handleUp() { dragging.current = false; }

  useEffect(() => {
    window.addEventListener("mouseup",  handleUp);
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mouseup",  handleUp);
      window.removeEventListener("touchend", handleUp);
    };
  }, []);

  const angle  = minsToAngle(minutes);
  const hx     = CX + R * Math.cos(angle);
  const hy     = CX + R * Math.sin(angle);

  const frac   = (minutes - MIN) / (MAX - MIN);
  const isFull = minutes >= MAX;
  const large  = frac >= 0.5 ? 1 : 0;
  // sweep-flag=1 → clockwise fill
  const arcD   = `M ${CX} ${CX - R} A ${R} ${R} 0 ${large} 1 ${hx} ${hy}`;

  // Tick marks every 5 mins — evenly spaced by fraction of full circle
  const ticks = [];
  for (let m = 5; m <= 60; m += 5) {
    const a   = -Math.PI/2 + (m/60) * 2 * Math.PI; // evenly around full circle
    const big = m % 15 === 0;
    const r1  = R + (big ? 10 : 6);
    const r2  = R + (big ? 20 : 13);
    ticks.push({
      x1: CX + r1 * Math.cos(a), y1: CX + r1 * Math.sin(a),
      x2: CX + r2 * Math.cos(a), y2: CX + r2 * Math.sin(a),
      big, m,
    });
  }

  const TEAL = "#3DB896";
  const BORDER = "#EAE5DB";
  const TEXT2  = "#5A6A7A";
  const TEXT3  = "#98A8B8";

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, userSelect:"none" }}>
      <svg
        ref={svgRef}
        width={SIZE} height={SIZE}
        style={{ cursor:"grab", touchAction:"none", overflow:"visible" }}
        onMouseDown={handleDown}
        onMouseMove={handleMove}
        onTouchStart={handleDown}
        onTouchMove={handleMove}
      >
        {/* Track */}
        <circle cx={CX} cy={CX} r={R} fill="none" stroke={BORDER} strokeWidth={12}/>

        {/* Filled arc — full circle when at max */}
        {isFull
          ? <circle cx={CX} cy={CX} r={R} fill="none" stroke={TEAL} strokeWidth={12}/>
          : <path d={arcD} fill="none" stroke={TEAL} strokeWidth={12} strokeLinecap="round"/>
        }

        {/* Tick marks */}
        {ticks.map(t => (
          <line key={t.m}
            x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke={minutes >= t.m ? TEAL : BORDER}
            strokeWidth={t.big ? 2 : 1.5}
            strokeLinecap="round"
          />
        ))}

        {/* Tick labels — pinned to exact cardinal positions for perfect visual alignment */}
        {[
          {m:60, angle:-Math.PI/2},      // 12 o'clock
          {m:15, angle:0},               // 3 o'clock
          {m:30, angle:Math.PI/2},       // 6 o'clock
          {m:45, angle:Math.PI},         // 9 o'clock
        ].map(({m,angle}) => {
          const lr = R + 32;
          return (
            <text key={m}
              x={CX + lr * Math.cos(angle)}
              y={CX + lr * Math.sin(angle)}
              textAnchor="middle"
              dy="0.35em"
              fontSize="11" fontWeight="600"
              fill={minutes >= m ? TEAL : TEXT3}
              fontFamily={sf}
            >{m}</text>
          );
        })}

        {/* Center display */}
        <text x={CX} y={CX - 12} textAnchor="middle" dominantBaseline="middle"
          fontSize="42" fontWeight="700" fill={TEAL} fontFamily={sf}
          style={{ letterSpacing:"-2px" }}
        >{minutes}</text>
        <text x={CX} y={CX + 22} textAnchor="middle" dominantBaseline="middle"
          fontSize="13" fill={TEXT2} fontFamily={sf}
        >{minutes === 1 ? "minute" : "minutes"}</text>

        {/* Drag handle */}
        <circle cx={hx} cy={hy} r={17} fill={TEAL} stroke="#fff" strokeWidth={3}
          style={{ filter:"drop-shadow(0 2px 8px rgba(61,184,150,.4))", cursor:"grab" }}
        />
      </svg>
      <p style={{ fontSize:12, color:TEXT3, fontFamily:sf }}>
        Drag the handle to set your duration
      </p>
    </div>
  );
}

/* ─── Main App ──────────────────────────────────────────────────────────────── */
export default function OhRight() {
  const [nudges,setNudges]   = useState([]);
  const [ready,setReady]     = useState(false);
  const [tab,setTab]         = useState("tasks");
  const [modal,setModal]     = useState(null);
  const [obPage,setObPage]   = useState(0);
  const [onboarded,setOnboarded] = useState(false);
  // Onboarding form state
  const [obName,setObName]       = useState("");
  const [obTraits,setObTraits]   = useState([]);
  const [obTasks,setObTasks]     = useState([{text:"",priority:"medium",timeSlot:"anytime"},{text:"",priority:"medium",timeSlot:"anytime"},{text:"",priority:"medium",timeSlot:"anytime"}]);
  const [obLib,setObLib]         = useState({text:"",category:"quote",author:""});
  const [obRippleSeen,setObRippleSeen] = useState(false);

  /* settings */
  const [name,setName]       = useState("");
  const [dyslexic,setDyslexic] = useState(false);
  const [tabLabels,setTabLabels] = useState(true);
  const [fApps,setFApps]     = useState(["Instagram","TikTok","YouTube"]);

  /* slot state */
  const blank = {morning:{e:null,picks:[],done:[]},afternoon:{e:null,picks:[],done:[]},evening:{e:null,picks:[],done:[]}};
  const [slots,setSlots]     = useState(blank);
  const [curSlot,setCurSlot] = useState(slot());
  const [sStep,setSStep]     = useState(0);
  const [sPool,setSPool]     = useState([]);
  const [active,setActive]   = useState(null);
  const [xInstr,setXInstr]   = useState(null);
  const [sessions,setSessions] = useState(0);

  /* add form */
  const blankForm = {text:"",author:"",category:"task",priority:"medium",contexts:[],timeSlot:"anytime",actionable:true,linkedInstructions:[]};
  const [form,setForm]       = useState(blankForm);
  const [fStep,setFStep]     = useState(1);
  const [fErr,setFErr]       = useState("");
  const [editId,setEditId]   = useState(null);
  const [ni,setNi]           = useState(""); /* new instruction text */
  const [aiILoad,setAiILoad] = useState(false);
  const [aiISugg,setAiISugg] = useState([]);
  const [aiIErr,setAiIErr]   = useState("");

  /* library */
  const [lSearch,setLSearch] = useState("");
  const [lType,setLType]     = useState("all");
  const [lFav,setLFav]       = useState(false);
  const [detItem,setDetItem] = useState(null);

  /* focus timer */
  const [fDur,setFDur]       = useState(1500);
  const [fLeft,setFLeft]     = useState(1500);
  const [fRun,setFRun]       = useState(false);
  const [fDone,setFDone]     = useState(false);
  const [fTask,setFTask]     = useState(null);
  const [fSettingsOpen,setFSettingsOpen] = useState(false);
  const [aiAL,setAiAL]       = useState(false);
  const tRef                 = useRef(null);

  /* ai chat */
  const [aiOpen,setAiOpen]   = useState(false);
  const [aiMsgs,setAiMsgs]   = useState([{role:"assistant",content:"Hey! I'm Ripple 👋\n\nI'm your Oh Right AI — here to help you plan, declutter your brain, or just talk through what's going on. Zero judgement, always.\n\nWhat's on your mind?"}]);
  const [aiIn,setAiIn]       = useState("");
  const [aiLoad,setAiLoad]   = useState(false);
  const aiEnd                = useRef(null);

  const [toast,setToast]     = useState(null);

  /* ── load / persist ── */
  useEffect(()=>{
    async function go(){
      try{
        const [nR,sR]=await Promise.all([localStorageGet("or9-n"),localStorageGet("or9-s")]);
        const ln=nR?.value?JSON.parse(nR.value):SAMPLES;
        setNudges(ln.length?ln:SAMPLES);
        if(sR?.value){
          const st=JSON.parse(sR.value);
          setOnboarded(!!st.ob); setName(st.name||""); setDyslexic(!!st.dx);
          setTabLabels(st.tl!==false); // default true
          if(st.fa) setFApps(st.fa);
          setSessions(st.ses||0);
          if(st.tk===todayKey()&&st.sl) setSlots(st.sl);
        }
      }catch{setNudges(SAMPLES);}
      setReady(true);
    }
    go();
  },[]);

  useEffect(()=>{if(ready)localStorageSet("or9-n",JSON.stringify(nudges));},[nudges,ready]);
  useEffect(()=>{if(ready)localStorageSet("or9-s",JSON.stringify({ob:onboarded,name,dx:dyslexic,tl:tabLabels,fa:fApps,ses:sessions,tk:todayKey(),sl:slots}));},[onboarded,name,dyslexic,tabLabels,fApps,sessions,slots,ready]);

  useEffect(()=>{
    if(!ready)return;
    setTimeout(()=>{
      if(!onboarded){setModal("ob");return;}
      const s=slot(); setCurSlot(s);
      if(!slots[s]?.picks?.length){setSStep(0);setModal("slot");return;}
    },1400);
  },[ready]);

  /* timer */
  useEffect(()=>{
    if(fRun&&!fDone){
      tRef.current=setInterval(()=>{
        setFLeft(s=>{
          if(s<=1){clearInterval(tRef.current);setFRun(false);setFDone(true);setSessions(n=>n+1);return 0;}
          return s-1;
        });
      },1000);
    }else clearInterval(tRef.current);
    return()=>clearInterval(tRef.current);
  },[fRun,fDone]);

  useEffect(()=>{aiEnd.current?.scrollIntoView({behavior:"smooth"});},[aiMsgs,aiLoad]);

  /* ── helpers ── */
  const pop=(msg,col)=>{setToast({msg,col:col||P.teal});setTimeout(()=>setToast(null),2500);};
  const C=(id)=>CATS.find(c=>c.id===id)||CATS[0];
  const S=(id)=>SLOTS.find(s=>s.id===id)||SLOTS[3];
  const E=(id)=>ENERGY.find(e=>e.id===id)||ENERGY[0];
  const PR=(id)=>PRIORITIES.find(p=>p.id===id)||PRIORITIES[1];
  const tasks=nudges.filter(n=>n.actionable);
  const libN=nudges.filter(n=>!n.actionable);
  const instrN=nudges.filter(n=>n.category==="instruction");
  const hr=new Date().getHours();
  const greet=hr<12?"Good morning":hr<17?"Good afternoon":"Good evening";
  const sd=slots[curSlot]||{e:null,picks:[],done:[]};
  const el=E(sd.e||"low");
  const si=S(curSlot);
  const CIRC=2*Math.PI*84;
  const tPct=fDur>0?(fDur-fLeft)/fDur:0;
  const sf=dyslexic?"'OpenDyslexic',Arial,sans-serif":"'DM Sans',sans-serif";
  const ff=dyslexic?"'OpenDyslexic',Arial,sans-serif":"'DM Serif Display',Georgia,serif";
  const todayDone=Object.values(slots).reduce((a,s)=>a+(s.done||[]).length,0);

  function upSlot(s,p){setSlots(prev=>({...prev,[s]:{...(prev[s]||{e:null,picks:[],done:[]}),...p}}));}

  function buildPool(lv){
    const done=slots[curSlot]?.done||[];
    const pool=tasks.filter(n=>!done.includes(n.id));
    const sm=pool.filter(n=>n.timeSlot===curSlot||n.timeSlot==="anytime");
    const use=sm.length>0?sm:pool;
    const po={high:0,medium:1,low:2};
    const sorted=[...use].sort((a,b)=>(po[a.priority||"medium"])-(po[b.priority||"medium"]));
    setSPool(sorted.slice(0,E(lv||"low").max*2+2));
  }

  function confirmE(lv){upSlot(curSlot,{e:lv,picks:[]});buildPool(lv);setSStep(1);}
  function togglePick(id){
    const picks=sd.picks||[]; const max=E(sd.e||"low").max;
    if(picks.includes(id))upSlot(curSlot,{picks:picks.filter(x=>x!==id)});
    else if(picks.length<max)upSlot(curSlot,{picks:[...picks,id]});
  }
  function confirmPicks(){
    if(!(sd.picks||[]).length)return;
    setActive(sd.picks[0]);setModal(null);
    pop(`${sd.picks.length} task${sd.picks.length!==1?"s":""} set ✓`);
  }
  function markDone(id){
    setNudges(p=>p.map(n=>n.id===id?{...n,doneCount:(n.doneCount||0)+1}:n));
    const np=(sd.picks||[]).filter(x=>x!==id);
    upSlot(curSlot,{picks:np,done:[...(sd.done||[]),id]});
    if(active===id)setActive(np[0]||null);
    pop(np.length===0?"All done! 🎉":"Done! Keep going 💪");
  }

  function openAdd(pre={}){setForm({...blankForm,...pre});setFStep(1);setFErr("");setEditId(null);setAiISugg([]);setAiIErr("");setNi("");setModal("add");}
  function openEdit(n){setForm({text:n.text,author:n.author||"",category:n.category,priority:n.priority||"medium",contexts:n.contexts||[],timeSlot:n.timeSlot||"anytime",actionable:n.actionable,linkedInstructions:n.linkedInstructions||[]});setFStep(1);setFErr("");setEditId(n.id);setAiISugg([]);setAiIErr("");setNi("");setModal(n.actionable?"add":"addlib");}
  function saveTask(){
    if(!form.text.trim()){setFErr("Write something first.");return;}
    if(editId){setNudges(p=>p.map(n=>n.id===editId?{...n,...form}:n));pop("Updated ✓");setEditId(null);}
    else{setNudges(p=>[{id:uid(),...form,favorite:false,createdAt:Date.now(),doneCount:0},...p]);pop("Saved ✓");}
    setForm(blankForm);setFStep(1);setNi("");setAiISugg([]);setModal(null);
  }
  function saveLib(){
    if(!form.text.trim()){setFErr("Write something first.");return;}
    if(editId){setNudges(p=>p.map(n=>n.id===editId?{...n,...form}:n));pop("Updated ✓");setEditId(null);}
    else{setNudges(p=>[{id:uid(),...form,actionable:false,favorite:false,createdAt:Date.now(),doneCount:0,linkedInstructions:[]},...p]);pop("Saved ✓");}
    setForm({...blankForm,category:"advice",actionable:false});setFErr("");setEditId(null);setModal(null);
  }
  function togLinked(iid){setForm(f=>{const l=f.linkedInstructions||[];return{...f,linkedInstructions:l.includes(iid)?l.filter(x=>x!==iid):[...l,iid]};});}
  function del(id){setNudges(p=>p.filter(n=>n.id!==id));pop("Deleted","#D94545");}
  function togFav(id){setNudges(p=>p.map(n=>n.id===id?{...n,favorite:!n.favorite}:n));}

  async function getAiInstrs(){
    if(!form.text.trim())return;
    setAiILoad(true);setAiIErr("");setAiISugg([]);
    try{
      const r=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:500,messages:[{role:"user",content:`ADHD coach. Task: "${form.text}". 3 short practical instructions. Respond ONLY with a JSON array of 3 strings.`}]})});
      const d=await r.json();
      const txt=d.content?.find(b=>b.type==="text")?.text||"[]";
      const p=JSON.parse(txt.replace(/```json|```/g,"").trim());
      if(Array.isArray(p))setAiISugg(p);else setAiIErr("Couldn't generate. Try again.");
    }catch{setAiIErr("Connection issue.");}
    setAiILoad(false);
  }
  function acceptInstr(text){
    const id=uid();
    setNudges(p=>[...p,{id,text,author:"Ripple",category:"instruction",priority:"medium",actionable:false,contexts:[],timeSlot:"anytime",favorite:false,createdAt:Date.now(),doneCount:0,linkedInstructions:[]}]);
    setForm(f=>({...f,linkedInstructions:[...(f.linkedInstructions||[]),id]}));
    setAiISugg(s=>s.filter(x=>x!==text));
  }
  function addNi(){
    if(!ni.trim())return;
    const id=uid();
    setNudges(p=>[...p,{id,text:ni.trim(),author:"",category:"instruction",priority:"medium",actionable:false,contexts:[],timeSlot:"anytime",favorite:false,createdAt:Date.now(),doneCount:0,linkedInstructions:[]}]);
    setForm(f=>({...f,linkedInstructions:[...(f.linkedInstructions||[]),id]}));
    setNi("");
  }
  async function suggestApps(){
    setAiAL(true);
    try{
      const tl=tasks.slice(0,5).map(t=>t.text).join(", ");
      const r=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:150,messages:[{role:"user",content:`ADHD, focusing on: "${tl}". Pick 3-4 most distracting from: ${APPS.join(", ")}. Respond ONLY with a JSON array.`}]})});
      const d=await r.json();
      const txt=d.content?.find(b=>b.type==="text")?.text||"[]";
      const p=JSON.parse(txt.replace(/```json|```/g,"").trim());
      if(Array.isArray(p)){setFApps(p);pop("Apps suggested ✓");}
    }catch{pop("Couldn't suggest","#D94545");}
    setAiAL(false);
  }
  async function sendAi(){
    if(!aiIn.trim()||aiLoad)return;
    const msg=aiIn.trim();setAiIn("");
    const msgs=[...aiMsgs,{role:"user",content:msg}];
    setAiMsgs(msgs);setAiLoad(true);
    const tl=tasks.slice(0,8).map(t=>`[${PR(t.priority).label}] ${t.text} (${t.timeSlot})`).join("\n")||"None yet.";
    try{
      const r=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1000,system:`You are a warm non-judgmental ADHD coach in Oh Right. Help users plan, declutter, break down tasks, find quotes/advice. Zero judgement.\n\nUser tasks:\n${tl}\n\nWhen suggesting items, end with:\n\`\`\`add\n[{"type":"task","text":"...","timeSlot":"morning|afternoon|evening|anytime","priority":"high|medium|low"},{"type":"quote","text":"...","author":"optional"},{"type":"advice","text":"..."},{"type":"affirmation","text":"..."}]\n\`\`\`\nOnly when you have specific items. Keep responses warm, concise.`,messages:msgs.map(m=>({role:m.role,content:m.content}))})});
      const d=await r.json();
      const raw=d.content?.find(b=>b.type==="text")?.text||"Sorry, try again!";
      const match=raw.match(/```add\n([\s\S]*?)```/);
      let items=[];let clean=raw;
      if(match){try{items=JSON.parse(match[1]);}catch{}clean=raw.replace(/```add\n[\s\S]*?```/,"").trim();}
      setAiMsgs(p=>[...p,{role:"assistant",content:clean,items}]);
    }catch{setAiMsgs(p=>[...p,{role:"assistant",content:"Sorry, connection issue!"}]);}
    setAiLoad(false);
  }
  function addAiItem(item){
    const isT=item.type==="task";
    setNudges(p=>[{id:uid(),text:item.text,author:item.author||"Ripple",category:isT?"task":item.type==="quote"?"quote":item.type==="affirmation"?"affirmation":"advice",priority:item.priority||"medium",actionable:isT,contexts:[],timeSlot:item.timeSlot||"anytime",favorite:false,createdAt:Date.now(),doneCount:0,linkedInstructions:[]},...p]);
    pop("Added ✓");
  }

  const libList=libN.filter(n=>{
    if(lType!=="all"&&n.category!==lType)return false;
    if(lFav&&!n.favorite)return false;
    if(lSearch&&!n.text.toLowerCase().includes(lSearch.toLowerCase())&&!(n.author||"").toLowerCase().includes(lSearch.toLowerCase()))return false;
    return true;
  });

  /* ── reusable small components ── */
  const PBadge=({pid})=>{const p=PR(pid||"medium");return(<span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"3px 8px",borderRadius:20,fontSize:11,fontWeight:700,background:p.bg,color:p.color}}>{p.emoji} {p.label}</span>);};
  const CatBadge=({cid})=>{const c=C(cid);return(<span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"3px 8px",borderRadius:20,fontSize:11,fontWeight:700,background:`${c.color}18`,color:c.color}}>{c.emoji} {c.label}</span>);};
  const Chip=({label,on,onClick,emoji})=>(<button onClick={onClick} style={{display:"inline-flex",alignItems:"center",gap:5,padding:"7px 13px",borderRadius:50,fontSize:12,fontWeight:500,border:`1.5px solid ${on?P.accent:P.border}`,color:on?P.accent:P.text2,background:on?P.sky:P.card,cursor:"pointer",transition:"all .15s",flexShrink:0,fontFamily:sf}}>{emoji&&<span>{emoji}</span>}{label}</button>);
  const Lbl=({children})=>(<p style={{fontSize:11,fontWeight:700,letterSpacing:"1.2px",textTransform:"uppercase",color:P.text3,marginBottom:10,fontFamily:sf}}>{children}</p>);
  const Divider=()=>(<div style={{height:1,background:P.border,margin:"20px 0"}}/>);

  /* ── CSS ── */
  const css=`
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
    @font-face{font-family:'OpenDyslexic';src:url('https://cdn.jsdelivr.net/npm/opendyslexic@0.91.12/fonts/OpenDyslexic-Regular.woff2') format('woff2');font-weight:normal;}
    @font-face{font-family:'OpenDyslexic';src:url('https://cdn.jsdelivr.net/npm/opendyslexic@0.91.12/fonts/OpenDyslexic-Bold.woff2') format('woff2');font-weight:bold;}
    *{box-sizing:border-box;margin:0;padding:0;}
    button{cursor:pointer;border:none;background:none;font-family:inherit;color:inherit;}
    input,textarea{outline:none;font-family:inherit;}

    .app{max-width:430px;margin:0 auto;min-height:100vh;background:${P.bg};color:${P.text1};font-family:${sf};position:relative;}

    /* Scrollable area with bottom padding for tab bar + AI bubble */
    .page{padding:0 ${T.px} 120px;animation:fu .28s cubic-bezier(.16,1,.3,1) both;}
    @keyframes fu{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}

    /* Page header — 3-column grid so avatar centres over first tab, + centres over AI island */
    .hdr{padding:52px 0 ${T.sp20};display:grid;grid-template-columns:76px 1fr 76px;align-items:center;margin:0 ${T.px};}
    .hdr-left{display:flex;align-items:center;justify-content:center;}
    .hdr-right{display:flex;align-items:center;justify-content:center;}
    .hdr-title{font-family:${ff};font-size:28px;letter-spacing:-0.8px;line-height:1;color:${P.text1};}
    .hdr-sub{font-size:12px;color:${P.text3};margin-top:3px;font-family:${sf};}

    /* Profile bubble — 50px, centered over first tab */
    .avatar{width:50px;height:50px;border-radius:50%;background:linear-gradient(135deg,${P.lavender},${P.accent});display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:18px;box-shadow:0 2px 12px rgba(74,143,231,.22);border:2px solid rgba(255,255,255,.9);cursor:pointer;transition:transform .2s;flex-shrink:0;}
    .avatar:hover{transform:scale(1.06);}
    /* Add button — same fixed size as avatar so they align */
    .add-btn{width:64px;height:50px;display:flex;align-items:center;justify-content:center;font-size:39px;font-weight:300;line-height:1;cursor:pointer;border:none;flex-shrink:0;transition:transform .15s;background:transparent;}
    .add-btn:hover{transform:scale(1.1);}
    .add-btn:active{transform:scale(.93);}

    /* Cards */
    .card{background:${P.card};border-radius:${T.r20};border:1px solid ${P.border};box-shadow:0 1px 4px rgba(0,0,0,.04);}
    .card-sm{background:${P.card};border-radius:${T.r14};border:1px solid ${P.border};box-shadow:0 1px 3px rgba(0,0,0,.04);}
    .card-tap:hover{transform:translateY(-1px);box-shadow:0 4px 16px rgba(0,0,0,.08);transition:all .15s;cursor:pointer;}
    .card-tap:active{transform:scale(.99);}

    /* Section spacing */
    .section{margin-bottom:${T.sp24};}
    .section-hdr{display:flex;justify-content:space-between;align-items:center;margin-bottom:${T.sp12};}

    /* Buttons — consistent sizing */
    .btn{border-radius:${T.r14};padding:14px ${T.sp20};font-size:14px;font-weight:700;width:100%;border:none;cursor:pointer;text-align:center;transition:all .15s;font-family:${sf};display:block;}
    .btn:active{transform:scale(.98);}
    .btn:disabled{background:${P.border};color:${P.text3};cursor:not-allowed;transform:none;}
    .btn-primary{background:${P.accent};color:#fff;}.btn-primary:hover{background:#3A7FD7;}
    .btn-teal{background:${P.teal};color:#fff;}.btn-teal:hover{background:#32A882;}
    .btn-ghost{background:transparent;border:1.5px solid ${P.border2};color:${P.text2};}.btn-ghost:hover{border-color:${P.text2};color:${P.text1};}
    .btn-sm{padding:10px ${T.sp16};font-size:13px;border-radius:${T.r12};}

    /* Input */
    .field{width:100%;padding:13px ${T.sp16};background:${P.bg};border:1.5px solid ${P.border};border-radius:${T.r14};color:${P.text1};font-size:14px;font-family:${sf};transition:border-color .15s;resize:none;line-height:1.5;}
    .field:focus{border-color:${P.accent};}
    .field::placeholder{color:${P.text3};}

    /* Horizontal scroll */
    .hscroll{display:flex;gap:8px;overflow-x:auto;padding-bottom:4px;scrollbar-width:none;}
    .hscroll::-webkit-scrollbar{display:none;}

    /* Tab row */
    .tabrow{display:flex;background:${P.bg};border-radius:${T.r12};padding:3px;border:1px solid ${P.border};gap:2px;}
    .tabtab{flex:1;padding:9px 4px;text-align:center;border-radius:10px;font-size:12px;font-weight:600;color:${P.text2};transition:all .15s;cursor:pointer;border:none;background:transparent;font-family:${sf};}
    .tabtab.on{background:${P.card};color:${P.text1};box-shadow:0 1px 4px rgba(0,0,0,.07);}

    /* Toggle */
    .toggle-wrap{display:flex;align-items:center;justify-content:space-between;background:${P.card};border:1.5px solid ${P.border};border-radius:${T.r14};padding:${T.sp14} ${T.sp16};cursor:pointer;transition:border-color .2s;}
    .toggle-wrap.on{border-color:${P.teal};}
    .tog{width:44px;height:24px;border-radius:12px;background:${P.border};position:relative;transition:background .2s;flex-shrink:0;}
    .tog.on{background:${P.teal};}
    .knob{position:absolute;top:3px;left:3px;width:18px;height:18px;border-radius:50%;background:#fff;transition:transform .2s;box-shadow:0 1px 3px rgba(0,0,0,.15);}
    .tog.on .knob{transform:translateX(20px);}

    /* ─ Nav row — holds both tab bar and AI island ─ */
    .nav-row{
      position:fixed;bottom:16px;
      left:50%;transform:translateX(-50%);
      width:calc(100% - 40px);max-width:390px;
      display:flex;gap:10px;align-items:center;
      z-index:100;
    }
    /* ─ Floating glass tab bar ─ */
    .tabbar{
      flex:1;
      height:64px;
      background:${P.glass};
      backdrop-filter:blur(28px) saturate(180%);
      -webkit-backdrop-filter:blur(28px) saturate(180%);
      border-radius:32px;
      border:1.5px solid ${P.glassB};
      box-shadow:0 6px 28px rgba(0,0,0,.10),0 2px 8px rgba(0,0,0,.05);
      display:flex;align-items:center;justify-content:space-around;
      padding:0 8px;
    }
    .tbi{
      display:flex;flex-direction:column;align-items:center;justify-content:center;
      gap:3px;
      height:46px;
      padding:0 12px;
      border-radius:20px;
      transition:background .18s ease;
      cursor:pointer;border:none;background:transparent;
      min-width:0;flex:1;
      flex-shrink:0;
    }
    .tbi.on{background:${P.text1};}
    .tbi-lbl{font-size:9px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:${P.text3};transition:color .18s;font-family:${sf};line-height:1;}
    .tbi.on .tbi-lbl{color:rgba(255,255,255,.75);}
    /* ─ AI island — perfect circle, same height as bar ─ */
    .ai-island{
      flex-shrink:0;
      width:64px;height:64px;border-radius:50%;
      background:${P.glass};
      backdrop-filter:blur(28px) saturate(180%);
      -webkit-backdrop-filter:blur(28px) saturate(180%);
      border:1.5px solid ${P.glassB};
      box-shadow:0 6px 28px rgba(0,0,0,.10),0 2px 8px rgba(0,0,0,.05);
      display:flex;align-items:center;justify-content:center;
      cursor:pointer;
      position:relative;
      transition:transform .2s cubic-bezier(.34,1.56,.64,1);
    }
    .ai-island:hover{transform:scale(1.06);}
    .ai-island:active{transform:scale(.93);}
    .ai-island.pulsing{animation:rp 1.4s ease-in-out infinite;}
    @keyframes rp{0%,100%{transform:scale(1);box-shadow:0 6px 28px rgba(0,0,0,.10),0 2px 8px rgba(0,0,0,.05)}50%{transform:scale(1.12);box-shadow:0 6px 28px rgba(74,143,231,.4),0 0 0 6px rgba(74,143,231,.15)}}
    .ai-ico{font-size:26px;line-height:1;}
    .ai-dot{position:absolute;top:9px;right:9px;width:9px;height:9px;background:${P.teal};border-radius:50%;border:2px solid ${P.bg};}

    /* ─ Modal / sheet ─ */
    .overlay{position:fixed;inset:0;background:rgba(0,0,0,.32);z-index:200;animation:oin .18s ease;}
    @keyframes oin{from{opacity:0}to{opacity:1}}
    .sheet{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:${P.card};border-radius:24px 24px 0 0;z-index:201;animation:shi .3s cubic-bezier(.16,1,.3,1);max-height:92vh;overflow-y:auto;padding:${T.sp16} ${T.px} 48px;box-shadow:0 -6px 40px rgba(0,0,0,.1);}
    @keyframes shi{from{transform:translateX(-50%) translateY(100%)}to{transform:translateX(-50%) translateY(0)}}
    .sheet-full{position:fixed;inset:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:${P.card};z-index:201;animation:shi .3s cubic-bezier(.16,1,.3,1);display:flex;flex-direction:column;overflow:hidden;}
    .handle{width:36px;height:4px;background:${P.border};border-radius:2px;margin:0 auto 20px;}
    .sheet-hdr{display:flex;justify-content:space-between;align-items:center;margin-bottom:${T.sp20};}

    /* ─ Today hero card ─ */
    .hero{background:linear-gradient(135deg,#ECF4FF,#E3F6EF);border-radius:${T.r24};padding:${T.sp20};border:1px solid ${P.border};}

    /* ─ Instruction accordion ─ */
    .instr-btn{width:100%;display:flex;align-items:center;justify-content:space-between;padding:${T.sp12} ${T.sp14};background:${P.bg};border:1.5px dashed ${P.border2};border-radius:${T.r14};cursor:pointer;transition:all .15s;margin-top:8px;}
    .instr-btn:hover,.instr-btn.open{border-color:#5A88C0;border-style:solid;background:#F2F6FC;}
    .instr-card{background:${P.bg};border-radius:${T.r12};border:1px solid ${P.border};padding:${T.sp12} ${T.sp14};margin-top:6px;}

    /* ─ Link card (instructions) ─ */
    .lk{background:${P.bg};border-radius:${T.r12};border:1.5px solid ${P.border};padding:${T.sp12} ${T.sp14};cursor:pointer;transition:all .15s;margin-bottom:8px;}
    .lk:hover,.lk.on{border-color:#5A88C0;background:#F2F6FC;}

    /* ─ AI suggestions ─ */
    .ai-sugg{background:${P.card};border:1px solid ${P.border};border-radius:${T.r12};padding:${T.sp12} ${T.sp14};margin-top:8px;}

    /* ─ AI chat bubbles ─ */
    .aib-ai{background:${P.card};border:1px solid ${P.border};border-radius:18px 18px 18px 4px;padding:12px 15px;max-width:85%;align-self:flex-start;box-shadow:0 1px 4px rgba(0,0,0,.04);}
    .aib-user{background:${P.accent};border-radius:18px 18px 4px 18px;padding:12px 15px;max-width:85%;align-self:flex-end;}

    /* ─ Onboard ─ */
    .ob-icon{width:76px;height:76px;border-radius:22px;display:flex;align-items:center;justify-content:center;font-size:38px;margin-bottom:${T.sp20};box-shadow:0 4px 18px rgba(0,0,0,.07);}
    .dot{width:8px;height:8px;border-radius:50%;background:${P.border};transition:all .3s;}
    .dot.on{width:22px;border-radius:4px;background:${P.accent};}

    /* ─ Step indicator ─ */
    .si{display:flex;align-items:center;gap:8px;margin-bottom:${T.sp20};}
    .sd{width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;flex-shrink:0;}
    .sd.ac{background:${P.accent};color:#fff;}.sd.dn{background:${P.teal};color:#fff;}.sd.ft{background:${P.border};color:${P.text3};}
    .sl{flex:1;height:2px;background:${P.border};border-radius:1px;}.sl.dn{background:${P.teal};}

    /* ─ Focus timer ─ */
    .timer-svg{transform:rotate(-90deg);}
    .timer-bg{fill:none;stroke:${P.border};stroke-width:12;}
    .timer-fg{fill:none;stroke-width:12;stroke-linecap:round;transition:stroke-dashoffset .9s ease,stroke .4s;}
    .tpulse{animation:tp 2s ease-in-out infinite;}
    @keyframes tp{0%,100%{opacity:1}50%{opacity:.55}}

    /* ─ Calendar blocks ─ */
    .cal{border-radius:${T.r20};padding:${T.sp16};margin-bottom:${T.sp12};border:1px solid transparent;}

    /* ─ Stats bars ─ */
    .bar-wrap{height:6px;background:${P.border};border-radius:3px;overflow:hidden;flex:1;}
    .bar-fill{height:100%;border-radius:3px;transition:width .6s ease;}

    /* ─ Slot pills ─ */
    .spill{display:inline-flex;align-items:center;gap:6px;padding:8px 14px;border-radius:50px;font-size:12px;font-weight:600;border:1.5px solid ${P.border};background:${P.card};transition:all .15s;cursor:pointer;color:${P.text2};white-space:nowrap;font-family:${sf};}
    .spill.on{border-color:var(--ac);background:var(--bg);color:var(--ac);}

    /* ─ AI add item card ─ */
    .ai-item{background:${P.sky};border:1px solid rgba(74,143,231,.2);border-radius:${T.r12};padding:${T.sp12} ${T.sp14};margin-top:8px;}
  `;

  /* ─── Render ─────────────────────────────────────────────────────────────── */
  return(
    <div style={{minHeight:"100vh",background:P.bg}}>
    <style>{css}</style>
    <div className="app">

      {/* SPLASH */}
      {!ready&&(
        <div style={{position:"fixed",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,background:P.bg,zIndex:999}}>
          <div style={{fontSize:50,animation:"fu .5s ease both"}}>💭</div>
          <div style={{textAlign:"center",animation:"fu .5s .15s ease both"}}>
            <h1 style={{fontSize:36,letterSpacing:"-1.5px",color:P.text1,fontFamily:ff}}>Oh Right<span style={{color:P.accent}}>.</span></h1>
            <p style={{color:P.text3,fontSize:13,marginTop:5,fontFamily:sf}}>Loading…</p>
          </div>
        </div>
      )}

      {ready&&<>

        {/* ════ TASKS ════════════════════════════════════════════════════════ */}
        {tab==="tasks"&&(
          <div>
            <div className="hdr">
              <div className="hdr-left">
                <button className="avatar" onClick={()=>setModal("profile")}>{name?name[0].toUpperCase():"?"}</button>
              </div>
              <div>
                <div className="hdr-title">Oh Right<span style={{color:P.accent}}>.</span></div>
                <div className="hdr-sub">{new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}</div>
              </div>
              <div className="hdr-right">
                <button className="add-btn" style={{color:P.accent}} onClick={()=>openAdd({timeSlot:curSlot})}>+</button>
              </div>
            </div>

            <div className="page">

              {/* Slot switcher */}
              <div className="section">
                <div className="hscroll">
                  {SLOTS.filter(s=>s.id!=="anytime").map(s=>{
                    const d=slots[s.id]||{e:null,picks:[],done:[]};
                    const dc=(d.done||[]).length;
                    const hp=(d.picks||[]).length>0;
                    const on=s.id===curSlot;
                    return(
                      <button key={s.id} className={`spill${on?" on":""}`} style={{"--ac":s.ac,"--bg":s.bg}} onClick={()=>{setCurSlot(s.id);if(!(slots[s.id]?.picks?.length)){setSStep(0);setModal("slot");}}}>
                        <span>{s.emoji}</span>
                        <span>{s.label}</span>
                        {dc>0&&<span style={{background:P.teal,color:"#fff",borderRadius:50,padding:"1px 7px",fontSize:10,fontWeight:700,fontFamily:sf}}>{dc}✓</span>}
                        {hp&&!dc&&<span style={{width:6,height:6,borderRadius:50,background:on?s.ac:P.text3}}/>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Stats row */}
              <div className="section" style={{display:"flex",gap:10}}>
                {[{l:"Tasks",v:tasks.length},{l:"Done today",v:todayDone},{l:"Sessions",v:sessions}].map(s=>(
                  <div key={s.l} className="card-sm" style={{flex:1,padding:"14px 10px",textAlign:"center"}}>
                    <div style={{fontSize:20,letterSpacing:"-1px",color:P.accent,fontWeight:700,fontFamily:sf}}>{s.v}</div>
                    <div style={{fontSize:10,color:P.text3,textTransform:"uppercase",letterSpacing:"1px",marginTop:3,fontWeight:700,fontFamily:sf}}>{s.l}</div>
                  </div>
                ))}
              </div>

              {/* Current slot section */}
              <div className="section">
                <div className="section-hdr">
                  <Lbl style={{margin:0}}>{si.emoji} {si.label} tasks</Lbl>
                  {sd.e&&(
                    <span style={{display:"inline-flex",alignItems:"center",gap:4,background:`${el.c}12`,border:`1px solid ${el.c}28`,borderRadius:50,padding:"3px 10px",fontSize:11,fontWeight:700,color:el.c,fontFamily:sf}}>{el.emoji} {el.label}</span>
                  )}
                </div>

                {(sd.picks||[]).length===0?(
                  <div className="card" style={{padding:24,textAlign:"center",borderStyle:"dashed"}}>
                    <div style={{fontSize:28,marginBottom:8}}>{si.emoji}</div>
                    <p style={{color:P.text2,fontSize:14,marginBottom:16,lineHeight:1.5,fontFamily:sf}}>No tasks set for {si.label.toLowerCase()} yet.</p>
                    <button className="btn btn-primary" onClick={()=>{setSStep(0);setModal("slot");}}>Set up {si.label.toLowerCase()} →</button>
                  </div>
                ):(
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {(sd.picks||[]).map((pid,idx)=>{
                      const n=nudges.find(x=>x.id===pid);if(!n)return null;
                      const isAct=active===pid;
                      const instrs=(n.linkedInstructions||[]).map(id=>nudges.find(x=>x.id===id)).filter(Boolean);
                      const tot=(sd.picks||[]).length;
                      return(
                        <div key={pid}>
                          {/* Collapsed */}
                          {!isAct&&(
                            <div className="card card-tap" style={{padding:"14px 16px",display:"flex",gap:10,alignItems:"center"}} onClick={()=>{setActive(pid);setXInstr(null);}}>
                              <PBadge pid={n.priority}/>
                              <p style={{flex:1,fontSize:14,fontStyle:"italic",lineHeight:1.45,color:P.text2,fontFamily:ff,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.text}</p>
                              <span style={{color:P.text3,fontSize:11,flexShrink:0,fontFamily:sf}}>tap ↓</span>
                            </div>
                          )}
                          {/* Expanded */}
                          {isAct&&(
                            <div>
                              <div className="hero">
                                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                                    <PBadge pid={n.priority}/>
                                    {tot>1&&<span style={{fontSize:11,color:P.text3,fontFamily:sf}}>Task {idx+1}/{tot}</span>}
                                  </div>
                                  <button onClick={()=>togFav(n.id)} style={{fontSize:16,lineHeight:1}}>{n.favorite?"❤️":"🤍"}</button>
                                </div>
                                <p style={{fontSize:18,fontStyle:"italic",lineHeight:1.55,color:P.text1,marginBottom:18,fontFamily:ff}}>{n.text}</p>
                                <div style={{display:"flex",gap:8}}>
                                  <button className="btn btn-teal btn-sm" style={{flex:2}} onClick={()=>markDone(pid)}>✓ Done</button>
                                  <button onClick={()=>{setFTask(pid);setTab("focus");}} style={{flex:1.5,background:P.amber,border:"none",borderRadius:12,fontSize:13,color:"#fff",fontWeight:700,cursor:"pointer",padding:"10px 8px",textAlign:"center",fontFamily:sf,transition:"all .15s"}}>⏱ Focus</button>
                                  <button onClick={()=>{setSStep(0);upSlot(curSlot,{picks:[],e:null});setModal("slot");}} style={{flex:1,background:"transparent",border:`1.5px solid ${P.border}`,borderRadius:12,fontSize:12,color:P.text2,cursor:"pointer",fontFamily:sf}} onMouseEnter={e=>e.currentTarget.style.borderColor=P.text2} onMouseLeave={e=>e.currentTarget.style.borderColor=P.border}>↺</button>
                                </div>
                              </div>
                              {instrs.length>0&&(
                                <div>
                                  <button className={`instr-btn${xInstr===pid?" open":""}`} onClick={()=>setXInstr(xInstr===pid?null:pid)}>
                                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                                      <span style={{fontSize:15}}>📋</span>
                                      <div>
                                        <p style={{fontWeight:600,fontSize:12,color:"#5A88C0",fontFamily:sf}}>How to do this</p>
                                        <p style={{fontSize:11,color:P.text3,marginTop:1,fontFamily:sf}}>{instrs.length} instruction{instrs.length!==1?"s":""}</p>
                                      </div>
                                    </div>
                                    <span style={{color:P.text3,fontSize:14,transition:"transform .2s",display:"inline-block",transform:xInstr===pid?"rotate(180deg)":"rotate(0deg)"}}>⌄</span>
                                  </button>
                                  {xInstr===pid&&instrs.map((ins,i)=>(
                                    <div key={ins.id} className="instr-card">
                                      <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                                        <div style={{width:20,height:20,borderRadius:50,background:"#E5EDF5",display:"flex",alignItems:"center",justifyContent:"center",color:"#5A88C0",fontSize:10,fontWeight:700,flexShrink:0,marginTop:1,fontFamily:sf}}>{i+1}</div>
                                        <p style={{fontSize:13,fontStyle:"italic",lineHeight:1.55,color:P.text2,fontFamily:ff}}>{ins.text}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Completed */}
              {(sd.done||[]).length>0&&(
                <div className="section">
                  <Lbl>Completed {si.label.toLowerCase()}</Lbl>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {(sd.done||[]).map(id=>{const n=nudges.find(x=>x.id===id);return n?(
                      <div key={id} style={{background:P.mint,borderRadius:12,padding:"10px 14px",display:"flex",gap:8,alignItems:"center"}}>
                        <span style={{color:P.teal,fontSize:13,fontWeight:700,flexShrink:0}}>✓</span>
                        <p style={{fontSize:13,color:P.teal,fontStyle:"italic",lineHeight:1.4,textDecoration:"line-through",textDecorationColor:`${P.teal}55`,fontFamily:ff}}>{n.text}</p>
                      </div>
                    ):null;})}
                  </div>
                </div>
              )}

              {/* Remaining tasks */}
              {tasks.filter(n=>!(sd.picks||[]).includes(n.id)&&!(sd.done||[]).includes(n.id)).length>0&&(
                <div className="section">
                  <div className="section-hdr">
                    <Lbl style={{margin:0}}>Other tasks</Lbl>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {tasks.filter(n=>!(sd.picks||[]).includes(n.id)&&!(sd.done||[]).includes(n.id)).map(n=>{
                      const ts=S(n.timeSlot||"anytime");
                      return(
                        <div key={n.id} className="card-sm card-tap" style={{padding:"12px 14px",display:"flex",gap:10,alignItems:"center"}}>
                          <PBadge pid={n.priority}/>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{fontSize:13,fontStyle:"italic",lineHeight:1.45,color:P.text2,fontFamily:ff,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.text}</p>
                            <div style={{display:"flex",gap:8,marginTop:3}}>
                              <span style={{fontSize:11,color:P.text3,fontFamily:sf}}>{ts.emoji} {ts.label}</span>
                              {n.linkedInstructions?.length>0&&<span style={{fontSize:11,color:"#5A88C0",fontFamily:sf}}>📋 {n.linkedInstructions.length}</span>}
                            </div>
                          </div>
                          <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                            <button onClick={()=>togFav(n.id)} style={{fontSize:13}}>{n.favorite?"❤️":"🤍"}</button>
                            <button onClick={()=>openEdit(n)} style={{color:P.text3,fontSize:12}}>✏️</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════ CALENDAR ═════════════════════════════════════════════════════ */}
        {tab==="calendar"&&(
          <div>
            <div className="hdr">
              <div className="hdr-left">
                <button className="avatar" onClick={()=>setModal("profile")}>{name?name[0].toUpperCase():"?"}</button>
              </div>
              <div>
                <div className="hdr-title">My Day</div>
                <div className="hdr-sub">{new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}</div>
              </div>
              <div className="hdr-right"/>
            </div>
            <div className="page">
              {SLOTS.filter(s=>s.id!=="anytime").map(s=>{
                const d=slots[s.id]||{e:null,picks:[],done:[]};
                const picks=(d.picks||[]).map(id=>nudges.find(n=>n.id===id)).filter(Boolean);
                const dones=(d.done||[]).map(id=>nudges.find(n=>n.id===id)).filter(Boolean);
                return(
                  <div key={s.id} className="cal" style={{background:s.bg,borderColor:`${s.ac}28`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:40,height:40,borderRadius:12,background:`${s.ac}20`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{s.emoji}</div>
                        <div>
                          <p style={{fontFamily:ff,fontSize:16,color:P.text1,letterSpacing:"-.3px"}}>{s.label}</p>
                          <p style={{fontSize:11,color:P.text3,marginTop:2,fontFamily:sf}}>{s.id==="morning"?"Before noon":s.id==="afternoon"?"12pm – 5pm":"After 5pm"}</p>
                        </div>
                      </div>
                      <button onClick={()=>{setCurSlot(s.id);setSStep(0);setModal("slot");}} style={{background:s.ac,color:"#fff",border:"none",borderRadius:20,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:sf}}>{picks.length||dones.length?"Edit":"Set up"}</button>
                    </div>

                    {picks.length===0&&dones.length===0&&(
                      <p style={{fontSize:13,color:P.text3,fontStyle:"italic",fontFamily:sf,padding:"2px 0"}}>Nothing set for {s.label.toLowerCase()} yet.</p>
                    )}

                    {picks.length>0&&(
                      <div style={{borderTop:`1px solid ${s.ac}25`,paddingTop:12,display:"flex",flexDirection:"column",gap:8}}>
                        {picks.map(n=>(
                          <div key={n.id} style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                            <PBadge pid={n.priority}/>
                            <p style={{fontSize:13,color:P.text1,flex:1,fontStyle:"italic",fontFamily:ff,lineHeight:1.45}}>{n.text}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {dones.length>0&&(
                      <div style={{borderTop:`1px solid ${s.ac}25`,paddingTop:10,marginTop:picks.length?8:0,display:"flex",flexDirection:"column",gap:6}}>
                        {dones.map(n=>(
                          <div key={n.id} style={{display:"flex",gap:8,alignItems:"center"}}>
                            <span style={{color:P.teal,fontSize:12,fontWeight:700,flexShrink:0}}>✓</span>
                            <p style={{fontSize:13,color:P.teal,fontStyle:"italic",textDecoration:"line-through",textDecorationColor:`${P.teal}55`,fontFamily:ff,lineHeight:1.4}}>{n.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ════ FOCUS ════════════════════════════════════════════════════════ */}
        {tab==="focus"&&(
          <div>
            <div className="hdr">
              <div className="hdr-left">
                <button className="avatar" onClick={()=>setModal("profile")}>{name?name[0].toUpperCase():"?"}</button>
              </div>
              <div>
                <div className="hdr-title">Focus</div>
                <div className="hdr-sub">{sessions} session{sessions!==1?"s":""} completed</div>
              </div>
              <div className="hdr-right"/>
            </div>
            <div className="page">

              {/* ── FOCUS TAB — vertically centred, same size both modes ── */}
              <div style={{
                display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                minHeight:"calc(100vh - 180px)",gap:28,
              }}>

                {/* Dial / Arc — identical 240px footprint */}
                {!fRun&&!fDone?(
                  <DialPicker minutes={Math.round(fDur/60)} onChange={m=>{setFDur(m*60);setFLeft(m*60);}} sf={sf} ff={ff}/>
                ):(
                  <div style={{position:"relative",width:290,height:290}}>
                    <svg width="290" height="290" className="timer-svg">
                      <circle className="timer-bg" cx="145" cy="145" r="114" strokeWidth="12"/>
                      <circle className={`timer-fg${fRun?" tpulse":""}`} cx="145" cy="145" r="114"
                        stroke={fLeft<60?P.coral:fLeft<fDur*.25?P.amber:P.accent}
                        strokeWidth="12"
                        strokeDasharray={`${2*Math.PI*114*(1-tPct)} ${2*Math.PI*114}`}
                        strokeDashoffset={2*Math.PI*114*tPct}
                      />
                    </svg>
                    <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4}}>
                      <p style={{fontSize:48,fontWeight:700,letterSpacing:"-2px",color:fLeft<60?P.coral:fLeft<fDur*.25?P.amber:P.text1,fontFamily:sf,lineHeight:1}}>{fmt(fLeft)}</p>
                      <p style={{fontSize:12,color:P.text3,fontFamily:sf}}>{fDone?"complete!":fRun?"focusing…":"paused"}</p>
                    </div>
                  </div>
                )}

                {/* Task label when running */}
                {(fRun||fDone)&&fTask&&nudges.find(n=>n.id===fTask)&&(
                  <div style={{background:P.sky,borderRadius:12,padding:"8px 16px",textAlign:"center",maxWidth:280,border:`1px solid rgba(74,143,231,.15)`,marginTop:-12}}>
                    <p style={{fontSize:13,color:P.text2,fontStyle:"italic",lineHeight:1.5,fontFamily:ff}}>"{nudges.find(n=>n.id===fTask)?.text.slice(0,55)}…"</p>
                  </div>
                )}

                {/* Blocked apps when running */}
                {fRun&&!fDone&&fApps.length>0&&(
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"center",maxWidth:280,marginTop:-12}}>
                    {fApps.slice(0,4).map(a=><span key={a} style={{fontSize:11,color:P.text3,background:P.card,padding:"3px 8px",borderRadius:50,border:`1px solid ${P.border}`,fontFamily:sf}}>🔒 {a}</span>)}
                    {fApps.length>4&&<span style={{fontSize:11,color:P.text3,fontFamily:sf}}>+{fApps.length-4}</span>}
                  </div>
                )}

                {/* ── Controls row ── */}
                {!fRun&&!fDone&&(
                  <div style={{display:"flex",alignItems:"center",gap:14}}>
                    {/* Start button */}
                    <button onClick={()=>{setFLeft(fDur);setFRun(true);setFDone(false);}} style={{
                      display:"flex",alignItems:"center",gap:10,
                      background:P.accent,color:"#fff",border:"none",
                      borderRadius:50,padding:"14px 28px",
                      fontSize:16,fontWeight:700,fontFamily:sf,
                      cursor:"pointer",
                      boxShadow:`0 4px 18px rgba(74,143,231,.3)`,
                      transition:"all .15s",
                    }}
                      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.04)"}
                      onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}
                    >
                      {/* Play icon */}
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="#fff">
                        <polygon points="4,2 14,8 4,14"/>
                      </svg>
                      Focus
                    </button>

                    {/* Gear bubble */}
                    <button onClick={()=>setFSettingsOpen(true)} style={{
                      width:52,height:52,borderRadius:"50%",
                      background:P.glass,
                      backdropFilter:"blur(20px)",
                      WebkitBackdropFilter:"blur(20px)",
                      border:`1.5px solid ${P.glassB}`,
                      boxShadow:"0 4px 16px rgba(0,0,0,.08)",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      cursor:"pointer",transition:"transform .15s",flexShrink:0,
                    }}
                      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.06)"}
                      onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={P.text2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                      </svg>
                    </button>
                  </div>
                )}

                {/* Pause/Resume/End when running */}
                {(fRun||(!fDone&&fLeft<fDur))&&!fDone&&(
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <button className="btn btn-ghost btn-sm" style={{minWidth:100}} onClick={()=>{clearInterval(tRef.current);setFRun(false);setFLeft(fDur);setFDone(false);}}>End</button>
                    {fRun
                      ?<button style={{display:"flex",alignItems:"center",gap:8,background:P.card,border:`1.5px solid ${P.border}`,borderRadius:50,padding:"12px 24px",fontSize:15,fontWeight:700,fontFamily:sf,cursor:"pointer"}} onClick={()=>setFRun(false)}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill={P.text1}><rect x="2" y="1" width="4" height="12" rx="1"/><rect x="8" y="1" width="4" height="12" rx="1"/></svg>
                        Pause
                      </button>
                      :<button style={{display:"flex",alignItems:"center",gap:8,background:P.accent,border:"none",borderRadius:50,padding:"12px 24px",fontSize:15,fontWeight:700,fontFamily:sf,color:"#fff",cursor:"pointer"}} onClick={()=>setFRun(true)}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="#fff"><polygon points="2,1 13,7 2,13"/></svg>
                        Resume
                      </button>
                    }
                  </div>
                )}

                {/* Done state */}
                {fDone&&(
                  <div style={{textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
                    <p style={{fontSize:44,marginBottom:4}}>🎉</p>
                    <h2 style={{fontSize:22,letterSpacing:"-.5px",fontFamily:ff}}>Session complete!</h2>
                    <p style={{fontSize:13,color:P.text2,fontFamily:sf,marginBottom:8}}>{sessions} session{sessions!==1?"s":""} total 💪</p>
                    <div style={{display:"flex",flexDirection:"column",gap:10,width:"100%",maxWidth:240}}>
                      {fTask&&<button className="btn btn-teal" onClick={()=>{markDone(fTask);setFDone(false);setFLeft(fDur);}}>✓ Mark task done</button>}
                      <button className="btn btn-primary" onClick={()=>{setFLeft(fDur);setFRun(true);setFDone(false);}}>🔄 Another round</button>
                      <button className="btn btn-ghost" onClick={()=>{setFDone(false);setFLeft(fDur);}}>Done</button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        )}

        {/* ════ LIBRARY ══════════════════════════════════════════════════════ */}
        {tab==="library"&&(
          <div>
            <div className="hdr">
              <div className="hdr-left">
                <button className="avatar" onClick={()=>setModal("profile")}>{name?name[0].toUpperCase():"?"}</button>
              </div>
              <div>
                <div className="hdr-title">Library</div>
                <div className="hdr-sub">{libN.length} item{libN.length!==1?"s":""} collected</div>
              </div>
              <div className="hdr-right">
                <button className="add-btn" style={{color:P.lavender}} onClick={()=>{setForm({...blankForm,category:"advice",actionable:false});setFErr("");setEditId(null);setModal("addlib");}}>+</button>
              </div>
            </div>
            <div className="page">
              {/* Search */}
              <div className="section" style={{position:"relative"}}>
                <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",color:P.text3,fontSize:14,pointerEvents:"none"}}>🔍</span>
                <input className="field" style={{paddingLeft:40,borderRadius:50}} placeholder="Search your library…" value={lSearch} onChange={e=>setLSearch(e.target.value)}/>
              </div>

              {/* Filters */}
              <div className="section">
                <div className="hscroll" style={{marginBottom:10}}>
                  {[{id:"all",label:"All",emoji:"📚"},{id:"instruction",label:"Instructions",emoji:"📋"},{id:"quote",label:"Quotes",emoji:"💬"},{id:"advice",label:"Advice",emoji:"💡"},{id:"affirmation",label:"Affirmations",emoji:"✨"}].map(t=>(
                    <Chip key={t.id} label={t.label} emoji={t.emoji} on={lType===t.id} onClick={()=>setLType(t.id)}/>
                  ))}
                </div>
                <Chip label="Favourites only" emoji="❤️" on={lFav} onClick={()=>setLFav(!lFav)}/>
              </div>

              <p style={{fontSize:12,color:P.text3,marginBottom:12,fontFamily:sf}}>{libList.length} item{libList.length!==1?"s":""}</p>

              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {libList.map(n=>{const c=C(n.category);return(
                  <div key={n.id} className="card card-tap" style={{padding:"15px 16px"}} onClick={()=>{setDetItem(n);setModal("detail");}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <CatBadge cid={n.category}/>
                      <div style={{display:"flex",gap:10,alignItems:"center"}}>
                        <button onClick={e=>{e.stopPropagation();togFav(n.id);}} style={{fontSize:14}}>{n.favorite?"❤️":"🤍"}</button>
                        <button onClick={e=>{e.stopPropagation();openEdit(n);}} style={{color:P.text3,fontSize:12}}>✏️</button>
                        <button onClick={e=>{e.stopPropagation();del(n.id);}} style={{color:P.text3,fontSize:12}}>🗑️</button>
                      </div>
                    </div>
                    <p style={{fontSize:14,fontStyle:"italic",lineHeight:1.6,color:P.text2,marginBottom:n.author?6:0,fontFamily:ff}}>{n.text}</p>
                    {n.author&&<p style={{fontSize:12,color:P.text3,fontFamily:sf}}>— {n.author}</p>}
                  </div>
                );})}
                {libList.length===0&&(
                  <div style={{textAlign:"center",padding:"48px 20px"}}>
                    <p style={{fontSize:32,marginBottom:10}}>📚</p>
                    <p style={{color:P.text3,fontStyle:"italic",fontFamily:ff,fontSize:15}}>Nothing here yet</p>
                    <p style={{color:P.text3,fontSize:13,marginTop:6,fontFamily:sf}}>Save quotes, advice, and affirmations to build your library.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ════ STATS ════════════════════════════════════════════════════════ */}
        {tab==="stats"&&(
          <div>
            <div className="hdr">
              <div className="hdr-left">
                <button className="avatar" onClick={()=>setModal("profile")}>{name?name[0].toUpperCase():"?"}</button>
              </div>
              <div>
                <div className="hdr-title">Progress</div>
                <div className="hdr-sub">Gentle reflection, no pressure</div>
              </div>
              <div className="hdr-right"/>
            </div>
            <div className="page" style={{display:"flex",flexDirection:"column",gap:16}}>

              {/* Today summary */}
              <div style={{background:"linear-gradient(135deg,#ECF4FF,#E3F6EF)",borderRadius:22,padding:20,border:`1px solid ${P.border}`}}>
                <Lbl>Today</Lbl>
                <div style={{display:"flex",gap:0}}>
                  {[{l:"Done",v:todayDone,c:P.teal},{l:"Sessions",v:sessions,c:P.accent},{l:"Saved",v:tasks.length,c:P.amber}].map((s,i)=>(
                    <div key={s.l} style={{flex:1,textAlign:"center",borderRight:i<2?`1px solid ${P.border}`:"none",padding:"0 8px"}}>
                      <div style={{fontSize:28,fontWeight:700,color:s.c,fontFamily:sf,letterSpacing:"-1px"}}>{s.v}</div>
                      <div style={{fontSize:11,color:P.text3,marginTop:4,fontFamily:sf,textTransform:"uppercase",letterSpacing:".5px"}}>{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Priority spread */}
              <div className="card" style={{padding:20}}>
                <Lbl>Tasks by priority</Lbl>
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  {PRIORITIES.map(p=>{
                    const cnt=tasks.filter(t=>(t.priority||"medium")===p.id).length;
                    const pct=tasks.length?cnt/tasks.length:0;
                    return(
                      <div key={p.id} style={{display:"flex",alignItems:"center",gap:12}}>
                        <span style={{fontSize:13,color:P.text2,width:68,flexShrink:0,fontFamily:sf}}>{p.emoji} {p.label}</span>
                        <div className="bar-wrap"><div className="bar-fill" style={{width:`${pct*100}%`,background:p.color}}/></div>
                        <span style={{fontSize:13,fontWeight:600,color:P.text2,width:20,textAlign:"right",fontFamily:sf}}>{cnt}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Time of day */}
              <div className="card" style={{padding:20}}>
                <Lbl>Tasks by time of day</Lbl>
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  {SLOTS.filter(s=>s.id!=="anytime").map(s=>{
                    const cnt=tasks.filter(n=>n.timeSlot===s.id).length;
                    const pct=tasks.length?cnt/tasks.length:0;
                    return(
                      <div key={s.id} style={{display:"flex",alignItems:"center",gap:12}}>
                        <span style={{fontSize:16,width:24,flexShrink:0}}>{s.emoji}</span>
                        <span style={{fontSize:13,color:P.text2,width:76,flexShrink:0,fontFamily:sf}}>{s.label}</span>
                        <div className="bar-wrap"><div className="bar-fill" style={{width:tasks.length?`${pct*100}%`:"0%",background:s.ac}}/></div>
                        <span style={{fontSize:13,fontWeight:600,color:P.text2,width:20,textAlign:"right",fontFamily:sf}}>{cnt}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Library */}
              <div className="card" style={{padding:20}}>
                <Lbl>Library</Lbl>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                  {CATS.filter(c=>!c.act).map(c=>{
                    const cnt=nudges.filter(n=>n.category===c.id).length;
                    return(
                      <div key={c.id} style={{textAlign:"center",background:`${c.color}10`,borderRadius:12,padding:"12px 6px",border:`1px solid ${c.color}20`}}>
                        <div style={{fontSize:18,marginBottom:4}}>{c.emoji}</div>
                        <div style={{fontSize:18,fontWeight:700,color:c.color,fontFamily:sf}}>{cnt}</div>
                        <div style={{fontSize:10,color:P.text3,marginTop:2,fontFamily:sf,lineHeight:1.2}}>{c.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Affirmation */}
              <div style={{background:P.yellow,borderRadius:18,padding:"18px 20px",border:`1px solid ${P.amber}25`}}>
                <p style={{fontStyle:"italic",fontSize:15,lineHeight:1.65,color:P.text1,fontFamily:ff}}>
                  {todayDone===0?"Every day you show up is progress. Even this counts.":todayDone===1?"One task done is one more than zero. That matters.":todayDone<=3?`${todayDone} tasks done today. That's real. Be proud.`:`${todayDone} tasks done today. You're genuinely doing great.`}
                </p>
              </div>

            </div>
          </div>
        )}

        {/* ════ NAV ROW — tab bar + AI island ══════════════════════════════ */}
        {!modal&&(
          <div className="nav-row">
            <nav className="tabbar">
              {[{id:"tasks",l:"Tasks",i:(on)=>Ico.tasks(on)},{id:"calendar",l:"Calendar",i:(on)=>Ico.cal(on)},{id:"focus",l:"Focus",i:(on)=>Ico.focus(on,tPct,fRun,(fRun||fDone||(!fDone&&fLeft<fDur)))},{id:"library",l:"Library",i:(on)=>Ico.lib(on)},{id:"stats",l:"Stats",i:(on)=>Ico.stats(on)}].map(t=>(
                <button key={t.id} className={`tbi${tab===t.id?" on":""}`} onClick={()=>setTab(t.id)}>
                  {t.i(tab===t.id)}
                  {tabLabels&&<span className="tbi-lbl">{t.l}</span>}
                </button>
              ))}
            </nav>
            <button className={`ai-island${!obRippleSeen&&onboarded?" pulsing":""}`} style={{position:"relative"}} onClick={()=>{setObRippleSeen(true);setModal("ai");}}>
              <span className="ai-ico">🤖</span>
              <div className="ai-dot"/>
              {/* Speech bubble — shows once after onboarding until tapped */}
              {!obRippleSeen&&onboarded&&(
                <div style={{
                  position:"absolute",
                  bottom:"calc(100% + 10px)",
                  right:0,
                  background:P.text1,
                  color:"#fff",
                  borderRadius:"14px 14px 4px 14px",
                  padding:"8px 13px",
                  fontSize:12,
                  fontWeight:600,
                  fontFamily:sf,
                  whiteSpace:"nowrap",
                  boxShadow:"0 4px 16px rgba(0,0,0,.18)",
                  animation:"fu .4s .6s cubic-bezier(.16,1,.3,1) both",
                  pointerEvents:"none",
                }}>
                  Hey{name.trim()?`, ${name.trim().split(" ")[0]}`:""}! Over here! 👋
                  {/* Tail */}
                  <div style={{
                    position:"absolute",
                    bottom:-7,right:14,
                    width:0,height:0,
                    borderLeft:"7px solid transparent",
                    borderRight:"0px solid transparent",
                    borderTop:`7px solid ${P.text1}`,
                  }}/>
                </div>
              )}
            </button>
          </div>
        )}

        {/* ════ MODALS ═══════════════════════════════════════════════════════ */}

        {/* Onboarding */}
        {modal==="ob"&&(()=>{
          const OB_TRAITS = [
            {id:"procrastinating",label:"Starting things",emoji:"😶‍🌫️"},
            {id:"overwhelmed",label:"Overwhelm",emoji:"🌊"},
            {id:"distracted",label:"Staying focused",emoji:"🌀"},
            {id:"stuck",label:"Getting unstuck",emoji:"🧱"},
            {id:"anxious",label:"Anxiety",emoji:"😬"},
            {id:"low-energy",label:"Low energy days",emoji:"🪫"},
            {id:"giving-up",label:"Following through",emoji:"🏳️"},
            {id:"forgetting",label:"Remembering things",emoji:"🫧"},
          ];

          function finishOnboarding(){
            // Save name
            if(obName.trim()) setName(obName.trim());
            // Save tasks
            const newTasks = obTasks.filter(t=>t.text.trim()).map(t=>({
              id:uid(), text:t.text.trim(), author:"", category:"task",
              actionable:true, priority:t.priority,
              contexts:obTraits, timeSlot:t.timeSlot,
              favorite:false, createdAt:Date.now(), doneCount:0, linkedInstructions:[],
            }));
            // Save library item
            const newLib = obLib.text.trim() ? [{
              id:uid(), text:obLib.text.trim(), author:obLib.author,
              category:obLib.category, actionable:false, priority:"medium",
              contexts:[], timeSlot:"anytime", favorite:true,
              createdAt:Date.now(), doneCount:0, linkedInstructions:[],
            }] : [];
            if(newTasks.length||newLib.length){
              setNudges(p=>[...newTasks,...newLib,...p.filter(n=>!SAMPLES.find(s=>s.id===n.id))]);
            }
            // Ripple intro message
            const rName = obName.trim()||"there";
            setAiMsgs([{
              role:"assistant",
              content:`Hey ${rName}! I'm Ripple 👋\n\nI'm your Oh Right companion — always here when you need to think out loud, plan your day, or just get unstuck.\n\nYou've just set up your first tasks. How are you feeling about getting started today?`
            }]);
            setOnboarded(true);
            setModal("slot");
          }

          const totalPages = 6;
          const prog = ((obPage+1)/totalPages)*100;

          return(
            <>
              <div className="overlay"/>
              <div className="sheet-full" style={{display:"flex",flexDirection:"column",overflow:"hidden"}}>

                {/* Progress bar */}
                <div style={{height:3,background:P.border,flexShrink:0}}>
                  <div style={{height:"100%",width:`${prog}%`,background:`linear-gradient(90deg,${P.teal},${P.accent})`,transition:"width .4s ease",borderRadius:2}}/>
                </div>

                <div style={{flex:1,overflowY:"auto",padding:"40px 24px 32px",display:"flex",flexDirection:"column"}}>

                  {/* ── Page 0: Welcome + name ── */}
                  {obPage===0&&(
                    <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"center",gap:28}}>
                      <div>
                        <div style={{fontSize:52,marginBottom:16}}>👋</div>
                        <h1 style={{fontSize:30,letterSpacing:"-.8px",lineHeight:1.15,fontFamily:ff,marginBottom:12}}>
                          Welcome to<br/>Oh Right<span style={{color:P.accent}}>.</span>
                        </h1>
                        <p style={{fontSize:15,color:P.text2,lineHeight:1.65,fontFamily:sf}}>
                          A home built for ADHD brains. No judgement, no streaks, no pressure. Just your stuff — finally in one place.
                        </p>
                      </div>
                      <div>
                        <p style={{fontSize:13,fontWeight:700,color:P.text3,letterSpacing:"1px",textTransform:"uppercase",marginBottom:10,fontFamily:sf}}>What should we call you?</p>
                        <input
                          className="field"
                          style={{fontSize:18,padding:"14px 18px"}}
                          placeholder="Your name…"
                          value={obName}
                          onChange={e=>setObName(e.target.value)}
                          autoFocus
                        />
                        <p style={{fontSize:12,color:P.text3,marginTop:8,fontFamily:sf,fontStyle:"italic"}}>First name, nickname, whatever feels right.</p>
                      </div>
                    </div>
                  )}

                  {/* ── Page 1: Struggles ── */}
                  {obPage===1&&(
                    <div style={{flex:1,display:"flex",flexDirection:"column",gap:20}}>
                      <div>
                        <div style={{fontSize:42,marginBottom:12}}>🧠</div>
                        <h2 style={{fontSize:26,letterSpacing:"-.5px",lineHeight:1.2,fontFamily:ff,marginBottom:10}}>
                          What does ADHD make hard for you?
                        </h2>
                        <p style={{fontSize:14,color:P.text2,lineHeight:1.6,fontFamily:sf}}>Pick everything that resonates. Oh Right uses this to surface the right tasks at the right time.</p>
                      </div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
                        {OB_TRAITS.map(t=>{
                          const on=obTraits.includes(t.id);
                          return(
                            <button key={t.id} onClick={()=>setObTraits(p=>on?p.filter(x=>x!==t.id):[...p,t.id])}
                              style={{padding:"10px 16px",borderRadius:50,fontSize:13,fontWeight:600,
                                background:on?P.sky:P.card,border:`1.5px solid ${on?P.accent:P.border}`,
                                color:on?P.accent:P.text2,cursor:"pointer",fontFamily:sf,
                                display:"flex",alignItems:"center",gap:7,transition:"all .15s"}}>
                              <span>{t.emoji}</span>{t.label}
                            </button>
                          );
                        })}
                      </div>
                      <p style={{fontSize:12,color:P.text3,fontFamily:sf,fontStyle:"italic"}}>Select as many as you like — or skip if none fit.</p>
                    </div>
                  )}

                  {/* ── Page 2: First task ── */}
                  {obPage===2&&(
                    <div style={{flex:1,display:"flex",flexDirection:"column",gap:20}}>
                      <div>
                        <div style={{fontSize:42,marginBottom:12}}>✅</div>
                        <h2 style={{fontSize:26,letterSpacing:"-.5px",lineHeight:1.2,fontFamily:ff,marginBottom:10}}>
                          What's one thing you've been putting off?
                        </h2>
                        <p style={{fontSize:14,color:P.text2,lineHeight:1.6,fontFamily:sf}}>The thing that's been sitting in the back of your head. Let's get it out.</p>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:14}}>
                        <textarea className="field" rows={3} placeholder="e.g. Reply to that email, book the appointment…"
                          value={obTasks[0].text} onChange={e=>setObTasks(p=>{const n=[...p];n[0]={...n[0],text:e.target.value};return n;})}
                          style={{fontSize:15}} autoFocus/>
                        <div>
                          <p style={{fontSize:11,fontWeight:700,color:P.text3,letterSpacing:"1px",textTransform:"uppercase",marginBottom:8,fontFamily:sf}}>Priority</p>
                          <div style={{display:"flex",gap:8}}>
                            {PRIORITIES.map(pr=>(
                              <button key={pr.id} onClick={()=>setObTasks(p=>{const n=[...p];n[0]={...n[0],priority:pr.id};return n;})}
                                style={{flex:1,padding:"10px 6px",borderRadius:12,fontSize:13,fontWeight:600,fontFamily:sf,cursor:"pointer",
                                  background:obTasks[0].priority===pr.id?pr.bg:P.card,
                                  border:`1.5px solid ${obTasks[0].priority===pr.id?pr.color:P.border}`,
                                  color:obTasks[0].priority===pr.id?pr.color:P.text2}}>
                                {pr.emoji} {pr.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p style={{fontSize:11,fontWeight:700,color:P.text3,letterSpacing:"1px",textTransform:"uppercase",marginBottom:8,fontFamily:sf}}>Best time</p>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                            {SLOTS.map(s=>(
                              <button key={s.id} onClick={()=>setObTasks(p=>{const n=[...p];n[0]={...n[0],timeSlot:s.id};return n;})}
                                style={{padding:"9px 8px",borderRadius:12,fontSize:12,fontWeight:600,fontFamily:sf,cursor:"pointer",
                                  background:obTasks[0].timeSlot===s.id?s.bg:P.card,
                                  border:`1.5px solid ${obTasks[0].timeSlot===s.id?s.ac:P.border}`,
                                  color:obTasks[0].timeSlot===s.id?s.ac:P.text2,
                                  display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                                <span>{s.emoji}</span>{s.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Page 3: More tasks ── */}
                  {obPage===3&&(
                    <div style={{flex:1,display:"flex",flexDirection:"column",gap:20}}>
                      <div>
                        <div style={{fontSize:42,marginBottom:12}}>📋</div>
                        <h2 style={{fontSize:26,letterSpacing:"-.5px",lineHeight:1.2,fontFamily:ff,marginBottom:10}}>
                          Got a couple more?
                        </h2>
                        <p style={{fontSize:14,color:P.text2,lineHeight:1.6,fontFamily:sf}}>Add up to 2 more tasks — or skip and add them later. No pressure.</p>
                      </div>
                      {[1,2].map(i=>(
                        <div key={i} style={{display:"flex",flexDirection:"column",gap:10}}>
                          <p style={{fontSize:11,fontWeight:700,color:P.text3,letterSpacing:"1px",textTransform:"uppercase",fontFamily:sf}}>Task {i+1} <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span></p>
                          <textarea className="field" rows={2} placeholder="Another thing on your mind…"
                            value={obTasks[i].text} onChange={e=>setObTasks(p=>{const n=[...p];n[i]={...n[i],text:e.target.value};return n;})}
                            style={{fontSize:14}}/>
                          {obTasks[i].text.trim()&&(
                            <div style={{display:"flex",gap:6}}>
                              {PRIORITIES.map(pr=>(
                                <button key={pr.id} onClick={()=>setObTasks(p=>{const n=[...p];n[i]={...n[i],priority:pr.id};return n;})}
                                  style={{flex:1,padding:"7px 4px",borderRadius:10,fontSize:11,fontWeight:600,fontFamily:sf,cursor:"pointer",
                                    background:obTasks[i].priority===pr.id?pr.bg:P.card,
                                    border:`1.5px solid ${obTasks[i].priority===pr.id?pr.color:P.border}`,
                                    color:obTasks[i].priority===pr.id?pr.color:P.text2}}>
                                  {pr.emoji} {pr.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── Page 4: Library seed ── */}
                  {obPage===4&&(
                    <div style={{flex:1,display:"flex",flexDirection:"column",gap:20}}>
                      <div>
                        <div style={{fontSize:42,marginBottom:12}}>💡</div>
                        <h2 style={{fontSize:26,letterSpacing:"-.5px",lineHeight:1.2,fontFamily:ff,marginBottom:10}}>
                          Something that keeps you going?
                        </h2>
                        <p style={{fontSize:14,color:P.text2,lineHeight:1.6,fontFamily:sf}}>A quote, a piece of advice, or an affirmation. This goes into your Library — your collection of things worth remembering.</p>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:14}}>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          {CATS.filter(c=>!c.act).map(c=>(
                            <button key={c.id} onClick={()=>setObLib(p=>({...p,category:c.id}))}
                              style={{padding:"7px 14px",borderRadius:50,fontSize:12,fontWeight:600,fontFamily:sf,cursor:"pointer",
                                background:obLib.category===c.id?`${c.color}18`:P.card,
                                border:`1.5px solid ${obLib.category===c.id?c.color:P.border}`,
                                color:obLib.category===c.id?c.color:P.text2}}>
                              {c.emoji} {c.label}
                            </button>
                          ))}
                        </div>
                        <textarea className="field" rows={4}
                          placeholder={obLib.category==="quote"?"Enter a quote…":obLib.category==="affirmation"?"Write an affirmation…":obLib.category==="instruction"?"A how-to tip…":"A piece of advice…"}
                          value={obLib.text} onChange={e=>setObLib(p=>({...p,text:e.target.value}))}
                          style={{fontSize:15}}/>
                        {obLib.category==="quote"&&(
                          <input className="field" placeholder="Author (optional)" value={obLib.author}
                            onChange={e=>setObLib(p=>({...p,author:e.target.value}))} style={{fontSize:14}}/>
                        )}
                        <p style={{fontSize:12,color:P.text3,fontFamily:sf,fontStyle:"italic"}}>This will be saved to your Library and starred. Totally optional.</p>
                      </div>
                    </div>
                  )}

                  {obPage===5&&(
                    <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center",textAlign:"center",gap:20}}>
                      <div style={{fontSize:60,marginBottom:4}}>🎉</div>
                      <div>
                        <h2 style={{fontSize:28,letterSpacing:"-.5px",lineHeight:1.2,fontFamily:ff,marginBottom:12}}>
                          {obName.trim()?`You're all set, ${obName.trim().split(" ")[0]}!`:"You're all set!"}
                        </h2>
                        <p style={{fontSize:15,color:P.text2,lineHeight:1.65,fontFamily:sf}}>
                          Oh Right is ready. Your tasks are waiting — let's go.
                        </p>
                      </div>
                    </div>
                  )}

                </div>

                {/* Bottom nav */}
                <div style={{padding:"16px 24px 44px",flexShrink:0,display:"flex",flexDirection:"column",gap:10,borderTop:`1px solid ${P.border}`}}>
                  {/* Progress dots */}
                  <div style={{display:"flex",gap:6,justifyContent:"center",marginBottom:4}}>
                    {[...Array(totalPages)].map((_,i)=>(
                      <div key={i} style={{width:i===obPage?20:7,height:7,borderRadius:4,background:i===obPage?P.accent:i<obPage?P.teal:P.border,transition:"all .3s"}}/>
                    ))}
                  </div>

                  <button className="btn btn-primary" onClick={()=>{
                    if(obPage<totalPages-1) setObPage(p=>p+1);
                    else finishOnboarding();
                  }}>
                    {obPage===0?"Let's go →":obPage===totalPages-1?"Open Oh Right →":"Continue →"}
                  </button>

                  {obPage>0&&obPage<totalPages-1&&(
                    <button onClick={()=>{
                      if(obPage<totalPages-1) setObPage(p=>p+1);
                      else finishOnboarding();
                    }} style={{fontSize:13,color:P.text3,fontFamily:sf,background:"none",border:"none",cursor:"pointer",padding:"4px"}}>
                      Skip this step
                    </button>
                  )}

                  {obPage>0&&(
                    <button onClick={()=>setObPage(p=>p-1)}
                      style={{fontSize:13,color:P.text3,fontFamily:sf,background:"none",border:"none",cursor:"pointer",padding:"4px"}}>
                      ← Back
                    </button>
                  )}
                </div>
              </div>
            </>
          );
        })()}

        {/* Slot energy + pick */}
        {modal==="slot"&&(
          <>
            <div className="overlay" onClick={()=>{if(slots[curSlot]?.picks?.length)setModal(null);}}/>
            <div className="sheet">
              <div className="handle"/>

              {sStep===0&&(
                <div>
                  <div style={{display:"inline-flex",alignItems:"center",gap:8,padding:"6px 12px",borderRadius:50,background:si.bg,marginBottom:16}}>
                    <span style={{fontSize:14}}>{si.emoji}</span>
                    <span style={{fontSize:12,fontWeight:700,color:si.ac,fontFamily:sf}}>{si.label} check-in</span>
                  </div>
                  <h2 style={{fontSize:24,lineHeight:1.2,letterSpacing:"-.5px",marginBottom:8,fontFamily:ff}}>How's your energy?</h2>
                  <p style={{fontSize:14,color:P.text2,lineHeight:1.6,marginBottom:20,fontFamily:sf}}>Be honest — there's no wrong answer.</p>
                  <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
                    {ENERGY.map(e=>(
                      <button key={e.id} onClick={()=>confirmE(e.id)} style={{background:P.card,border:`1.5px solid ${P.border}`,borderRadius:16,padding:"16px",textAlign:"left",cursor:"pointer",transition:"all .2s",display:"flex",alignItems:"center",gap:14,boxShadow:"0 1px 4px rgba(0,0,0,.04)"}}
                        onMouseEnter={ev=>{ev.currentTarget.style.borderColor=e.c;ev.currentTarget.style.background=`${e.c}0E`;}}
                        onMouseLeave={ev=>{ev.currentTarget.style.borderColor=P.border;ev.currentTarget.style.background=P.card;}}>
                        <div style={{width:44,height:44,borderRadius:12,background:`${e.c}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{e.emoji}</div>
                        <div style={{flex:1}}>
                          <p style={{fontSize:16,letterSpacing:"-.2px",marginBottom:2,fontFamily:ff}}>{e.label}</p>
                          <p style={{fontSize:12,color:P.text2,lineHeight:1.4,fontFamily:sf}}>{e.sub}</p>
                        </div>
                        <span style={{color:P.text3,fontSize:16}}>→</span>
                      </button>
                    ))}
                  </div>
                  <p style={{fontSize:12,color:P.text3,textAlign:"center",lineHeight:1.65,fontStyle:"italic",fontFamily:sf}}>Some days one task is the win. That's enough.</p>
                </div>
              )}

              {sStep===1&&(()=>{
                const max=E(sd.e||"low").max;
                const picks=sd.picks||[];
                return(
                  <div>
                    <button onClick={()=>{setSStep(0);upSlot(curSlot,{picks:[]});}} style={{fontSize:13,color:P.text2,marginBottom:16,display:"flex",alignItems:"center",gap:5,cursor:"pointer",background:"none",border:"none",fontWeight:500,fontFamily:sf}}>← Change energy</button>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                      <div style={{width:38,height:38,borderRadius:11,background:`${el.c}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{el.emoji}</div>
                      <div>
                        <h3 style={{fontSize:20,letterSpacing:"-.3px",fontFamily:ff}}>{el.label} day</h3>
                        <p style={{fontSize:12,color:P.text2,marginTop:2,fontFamily:sf}}>Pick up to <strong style={{color:el.c}}>{max} task{max!==1?"s":""}</strong> · high priority shown first</p>
                      </div>
                    </div>

                    {picks.length>0&&(
                      <div style={{display:"flex",gap:5,alignItems:"center",background:`${el.c}10`,border:`1px solid ${el.c}28`,borderRadius:50,padding:"7px 12px",marginBottom:16}}>
                        <span style={{fontSize:12}}>{el.emoji}</span>
                        <span style={{fontSize:12,color:el.c,fontWeight:600,fontFamily:sf}}>{picks.length} of {max} selected</span>
                        <div style={{display:"flex",gap:3,marginLeft:4}}>
                          {[...Array(max)].map((_,i)=><div key={i} style={{width:7,height:7,borderRadius:50,background:i<picks.length?el.c:P.border,transition:"background .2s"}}/>)}
                        </div>
                      </div>
                    )}

                    {sPool.length===0?(
                      <div className="card" style={{padding:24,textAlign:"center",borderStyle:"dashed"}}>
                        <p style={{color:P.text2,fontSize:13,marginBottom:14,fontFamily:sf}}>No tasks saved yet.</p>
                        <button className="btn btn-primary" onClick={()=>{setModal("add");}}>Add a task →</button>
                      </div>
                    ):(
                      <>
                        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
                          {sPool.map(n=>{
                            const sel=picks.includes(n.id);
                            const dis=!sel&&picks.length>=max;
                            return(
                              <div key={n.id} onClick={()=>{if(!dis)togglePick(n.id);}} style={{background:sel?`${el.c}0A`:P.card,borderRadius:16,border:`1.5px solid ${sel?el.c:P.border}`,padding:"12px 14px",cursor:dis?"not-allowed":"pointer",opacity:dis?.4:1,transition:"all .2s"}}>
                                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                                  <div style={{display:"flex",gap:6,alignItems:"center"}}><PBadge pid={n.priority}/><span style={{fontSize:11,color:P.text3,fontFamily:sf}}>{S(n.timeSlot||"anytime").emoji}</span></div>
                                  <div style={{width:20,height:20,borderRadius:50,border:`2px solid ${sel?el.c:P.border}`,background:sel?el.c:"transparent",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:700,flexShrink:0}}>{sel?"✓":""}</div>
                                </div>
                                <p style={{fontSize:14,fontStyle:"italic",lineHeight:1.5,color:sel?P.text1:P.text2,fontFamily:ff}}>{n.text}</p>
                              </div>
                            );
                          })}
                        </div>
                        <div style={{display:"flex",gap:10}}>
                          <button className="btn btn-ghost btn-sm" style={{flex:1}} onClick={()=>buildPool(sd.e)}>🔀 More</button>
                          <button className="btn btn-sm" disabled={!picks.length} onClick={confirmPicks} style={{flex:2,background:picks.length?el.c:P.border,color:picks.length?"#fff":P.text3,fontFamily:sf,fontSize:14,fontWeight:700,cursor:picks.length?"pointer":"not-allowed",borderRadius:12,border:"none"}}>
                            {picks.length?`Let's go →`:"Select a task →"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </>
        )}

        {/* Add task */}
        {modal==="add"&&(
          <>
            <div className="overlay" onClick={()=>setModal(null)}/>
            <div className="sheet">
              <div className="handle"/>
              <div className="sheet-hdr">
                <h3 style={{fontSize:22,fontFamily:ff}}>{editId?"Edit task":"New task"}</h3>
                <button onClick={()=>{setModal(null);setEditId(null);}} style={{color:P.text3,fontSize:18,lineHeight:1}}>✕</button>
              </div>

              {fStep===1&&(
                <div style={{display:"flex",flexDirection:"column",gap:18}}>
                  <div>
                    <Lbl>Priority</Lbl>
                    <div style={{display:"flex",gap:8}}>
                      {PRIORITIES.map(p=>(
                        <button key={p.id} onClick={()=>setForm(f=>({...f,priority:p.id}))} style={{flex:1,padding:"11px 8px",borderRadius:12,fontSize:13,fontWeight:600,background:form.priority===p.id?p.bg:P.bg,border:`1.5px solid ${form.priority===p.id?p.color:P.border}`,color:form.priority===p.id?p.color:P.text2,cursor:"pointer",textAlign:"center",fontFamily:sf}}>{p.emoji} {p.label}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Lbl>Best time</Lbl>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      {SLOTS.map(s=>(
                        <button key={s.id} onClick={()=>setForm(f=>({...f,timeSlot:s.id}))} style={{padding:"10px 8px",borderRadius:12,fontSize:12,fontWeight:600,background:form.timeSlot===s.id?s.bg:P.card,border:`1.5px solid ${form.timeSlot===s.id?s.ac:P.border}`,color:form.timeSlot===s.id?s.ac:P.text2,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5,fontFamily:sf}}>
                          <span>{s.emoji}</span>{s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Lbl>Task *</Lbl>
                    <textarea className="field" rows={4} placeholder="What do you need to do?" value={form.text} onChange={e=>{setForm(f=>({...f,text:e.target.value}));setFErr("");}}/>
                    {fErr&&<p style={{fontSize:12,color:P.coral,marginTop:6,fontWeight:600,fontFamily:sf}}>{fErr}</p>}
                  </div>
                  <div>
                    <Lbl>Surface when I'm… <span style={{textTransform:"none",letterSpacing:0,fontWeight:400,fontFamily:sf}}>(optional)</span></Lbl>
                    <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                      {MOODS.map(m=>{const s=(form.contexts||[]).includes(m.id);return(
                        <Chip key={m.id} label={m.label} emoji={m.emoji} on={s} onClick={()=>setForm(f=>({...f,contexts:s?f.contexts.filter(x=>x!==m.id):[...f.contexts,m.id]}))}/>
                      );})}
                    </div>
                  </div>
                  <button className="btn btn-primary" onClick={()=>{if(!form.text.trim()){setFErr("Write something first.");return;}setAiISugg([]);setAiIErr("");setFStep(2);}}>Next: Add instructions →</button>
                </div>
              )}

              {fStep===2&&(
                <div style={{display:"flex",flexDirection:"column",gap:16}}>
                  <div className="card-sm" style={{padding:"12px 14px",borderLeft:`3px solid ${P.teal}`}}>
                    <div style={{marginBottom:6}}><PBadge pid={form.priority}/></div>
                    <p style={{fontSize:14,fontStyle:"italic",lineHeight:1.5,color:P.text2,fontFamily:ff}}>{form.text}</p>
                  </div>

                  {/* AI instructions */}
                  <div style={{background:P.sky,border:`1px solid rgba(74,143,231,.2)`,borderRadius:14,padding:"14px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <div>
                        <p style={{fontWeight:700,fontSize:13,color:P.accent,fontFamily:sf}}>✦ AI instructions</p>
                        <p style={{fontSize:11,color:P.text3,marginTop:2,fontFamily:sf}}>Generate how-to steps for this task</p>
                      </div>
                      <button onClick={getAiInstrs} disabled={aiILoad} style={{background:P.accent,color:"#fff",borderRadius:50,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:aiILoad?"not-allowed":"pointer",border:"none",fontFamily:sf,opacity:aiILoad?.7:1}}>{aiILoad?"Thinking…":"Suggest"}</button>
                    </div>
                    {aiIErr&&<p style={{fontSize:12,color:P.coral,marginTop:8,fontFamily:sf}}>{aiIErr}</p>}
                    {aiISugg.map((s,i)=>(
                      <div key={i} className="ai-sugg" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                        <p style={{fontSize:13,fontStyle:"italic",lineHeight:1.5,color:P.text2,flex:1,fontFamily:ff}}>{s}</p>
                        <button onClick={()=>acceptInstr(s)} style={{background:P.sky,color:P.accent,border:`1px solid rgba(74,143,231,.25)`,borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0,fontFamily:sf}}>+ Add</button>
                      </div>
                    ))}
                  </div>

                  {/* Write own */}
                  <div style={{background:P.bg,border:`1.5px dashed ${P.border2}`,borderRadius:14,padding:"12px 14px"}}>
                    <Lbl>Write your own</Lbl>
                    <div style={{display:"flex",gap:8}}>
                      <textarea className="field" rows={2} placeholder='"Start with just two minutes…"' value={ni} onChange={e=>setNi(e.target.value)} style={{flex:1,padding:"9px 12px",fontSize:13}}/>
                      <button onClick={addNi} style={{background:"#5A88C0",color:"#fff",border:"none",borderRadius:10,padding:"0 13px",fontWeight:700,fontSize:13,cursor:"pointer",flexShrink:0,fontFamily:sf}}>Add</button>
                    </div>
                  </div>

                  {/* Link existing */}
                  {instrN.filter(i=>!(form.linkedInstructions||[]).includes(i.id)).length>0&&(
                    <div>
                      <Lbl>Or link saved</Lbl>
                      {instrN.filter(i=>!(form.linkedInstructions||[]).includes(i.id)).map(ins=>(
                        <div key={ins.id} className="lk" onClick={()=>togLinked(ins.id)}>
                          <div style={{display:"flex",gap:10}}>
                            <div style={{width:16,height:16,borderRadius:50,border:`2px solid ${P.border}`,flexShrink:0,marginTop:2}}/>
                            <p style={{fontSize:13,fontStyle:"italic",color:P.text2,fontFamily:ff,lineHeight:1.45}}>{ins.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Linked */}
                  {(form.linkedInstructions||[]).length>0&&(
                    <div>
                      <Lbl style={{color:P.teal}}>Linked ({form.linkedInstructions.length})</Lbl>
                      {(form.linkedInstructions||[]).map(id=>{const ins=nudges.find(n=>n.id===id);return ins?(
                        <div key={id} className="lk on" style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                          <p style={{fontSize:13,fontStyle:"italic",color:P.text2,flex:1,fontFamily:ff,lineHeight:1.45}}>{ins.text}</p>
                          <button onClick={()=>togLinked(id)} style={{color:P.text3,fontSize:12,marginLeft:10,cursor:"pointer",background:"none",border:"none"}}>✕</button>
                        </div>
                      ):null;})}
                    </div>
                  )}

                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    <button className="btn btn-primary" onClick={saveTask}>{editId?"Update ✓":"Save task ✓"}{(form.linkedInstructions||[]).length>0?` — ${form.linkedInstructions.length} linked`:""}</button>
                    <button onClick={()=>setFStep(1)} style={{color:P.text3,fontSize:13,fontWeight:500,cursor:"pointer",background:"none",border:"none",padding:"6px",textAlign:"center",fontFamily:sf}}>← Back</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Add library item */}
        {modal==="addlib"&&(
          <>
            <div className="overlay" onClick={()=>{setModal(null);setEditId(null);}}/>
            <div className="sheet">
              <div className="handle"/>
              <div className="sheet-hdr">
                <h3 style={{fontSize:22,fontFamily:ff}}>{editId?"Edit item":"Add to Library"}</h3>
                <button onClick={()=>{setModal(null);setEditId(null);}} style={{color:P.text3,fontSize:18}}>✕</button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:18}}>
                <div>
                  <Lbl>Type</Lbl>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                    {CATS.filter(c=>!c.act).map(c=>(
                      <button key={c.id} onClick={()=>setForm(f=>({...f,category:c.id}))} style={{padding:"8px 14px",borderRadius:50,fontSize:13,fontWeight:600,background:form.category===c.id?`${c.color}18`:P.card,border:`1.5px solid ${form.category===c.id?c.color:P.border}`,color:form.category===c.id?c.color:P.text2,cursor:"pointer",fontFamily:sf}}>{c.emoji} {c.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Lbl>{form.category==="quote"?"Quote *":form.category==="affirmation"?"Affirmation *":form.category==="instruction"?"Instruction *":"Advice *"}</Lbl>
                  <textarea className="field" rows={5} placeholder={form.category==="quote"?"Enter a quote…":form.category==="affirmation"?"Write an affirmation…":form.category==="instruction"?"How to approach a task…":"Write some advice…"} value={form.text} onChange={e=>{setForm(f=>({...f,text:e.target.value}));setFErr("");}}/>
                  {fErr&&<p style={{fontSize:12,color:P.coral,marginTop:6,fontWeight:600,fontFamily:sf}}>{fErr}</p>}
                </div>
                <div>
                  <Lbl>Author / source <span style={{textTransform:"none",letterSpacing:0,fontWeight:400,fontFamily:sf}}>(optional)</span></Lbl>
                  <input className="field" placeholder="e.g. James Clear, my therapist, Mum…" value={form.author||""} onChange={e=>setForm(f=>({...f,author:e.target.value}))}/>
                </div>
                <button className="btn btn-primary" onClick={saveLib}>{editId?"Update ✓":"Save to Library ✓"}</button>
              </div>
            </div>
          </>
        )}

        {/* Detail */}
        {modal==="detail"&&detItem&&(()=>{const n=detItem;const c=C(n.category);return(
          <>
            <div className="overlay" onClick={()=>setModal(null)}/>
            <div className="sheet">
              <div className="handle"/>
              <CatBadge cid={n.category}/>
              <p style={{fontSize:22,fontStyle:"italic",lineHeight:1.55,color:P.text1,margin:"14px 0",fontFamily:ff}}>"{n.text}"</p>
              {n.author&&<p style={{fontSize:14,color:P.text3,marginBottom:20,fontFamily:sf}}>— {n.author}</p>}
              <div style={{display:"flex",gap:10}}>
                <button className="btn btn-ghost btn-sm" style={{flex:1}} onClick={()=>togFav(n.id)}>{n.favorite?"💔 Unfav":"❤️ Fav"}</button>
                <button className="btn btn-ghost btn-sm" style={{flex:1}} onClick={()=>openEdit(n)}>✏️ Edit</button>
                <button className="btn btn-ghost btn-sm" style={{flex:"0 0 44px",borderColor:"rgba(217,69,69,.35)",color:"#D94545"}} onClick={()=>{del(n.id);setModal(null);}}>🗑️</button>
              </div>
            </div>
          </>
        );})()}

        {/* Profile / settings */}
        {modal==="profile"&&(
          <>
            <div className="overlay" onClick={()=>setModal(null)}/>
            <div className="sheet">
              <div className="handle"/>
              <div className="sheet-hdr">
                <h3 style={{fontSize:22,fontFamily:ff}}>Settings</h3>
                <button onClick={()=>setModal(null)} style={{color:P.text3,fontSize:18}}>✕</button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:18}}>
                <div>
                  <Lbl>Your name</Lbl>
                  <input className="field" placeholder="What should we call you?" value={name} onChange={e=>setName(e.target.value)}/>
                </div>
                <Divider/>
                <div>
                  <Lbl>Accessibility</Lbl>
                  <div className={`toggle-wrap${dyslexic?" on":""}`} style={{marginBottom:10}} onClick={()=>setDyslexic(!dyslexic)}>
                    <div>
                      <p style={{fontWeight:600,fontSize:14,color:dyslexic?P.teal:P.text1,fontFamily:sf}}>OpenDyslexic font</p>
                      <p style={{fontSize:12,color:P.text3,marginTop:3,lineHeight:1.4,fontFamily:sf}}>Designed for easier reading with dyslexia</p>
                    </div>
                    <div className={`tog${dyslexic?" on":""}`}><div className="knob"/></div>
                  </div>
                  {dyslexic&&<div style={{background:P.mint,borderRadius:10,padding:"10px 14px",marginBottom:10,border:`1px solid ${P.teal}28`}}><p style={{fontSize:13,color:P.teal,fontFamily:sf}}>✓ OpenDyslexic active across the whole app.</p></div>}
                  <div className={`toggle-wrap${tabLabels?" on":""}`} onClick={()=>setTabLabels(!tabLabels)}>
                    <div>
                      <p style={{fontWeight:600,fontSize:14,color:tabLabels?P.teal:P.text1,fontFamily:sf}}>Tab bar labels</p>
                      <p style={{fontSize:12,color:P.text3,marginTop:3,lineHeight:1.4,fontFamily:sf}}>Show text labels under the tab icons</p>
                    </div>
                    <div className={`tog${tabLabels?" on":""}`}><div className="knob"/></div>
                  </div>
                </div>
                <Divider/>
                <div style={{background:P.yellow,borderRadius:16,padding:"16px 18px",border:`1px solid ${P.amber}25`}}>
                  <p style={{fontFamily:ff,fontSize:18,letterSpacing:"-.5px",marginBottom:6}}>Oh Right<span style={{color:P.accent}}>.</span></p>
                  <p style={{fontSize:13,color:P.text2,lineHeight:1.65,fontFamily:sf}}>A home for people with ADHD — where there's no judgement and everybody understands each other.</p>
                  <p style={{fontSize:11,color:P.text3,marginTop:8,fontFamily:sf}}>Version 0.1 — Prototype</p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* AI Chat */}
        {modal==="ai"&&(
          <>
            <div className="overlay" onClick={()=>setModal(null)}/>
            <div className="sheet-full">
              {/* Header */}
              <div style={{padding:"48px 20px 14px",background:P.card,borderBottom:`1px solid ${P.border}`,flexShrink:0}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:42,height:42,borderRadius:14,background:P.sky,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,border:`1px solid ${P.border}`}}>🤖</div>
                    <div>
                      <h2 style={{fontSize:20,letterSpacing:"-.3px",fontFamily:ff}}>Ripple</h2>
                      <p style={{fontSize:12,color:P.text3,marginTop:2,fontFamily:sf}}>Your ADHD companion</p>
                    </div>
                  </div>
                  <button onClick={()=>setModal(null)} style={{color:P.text3,fontSize:20,lineHeight:1,width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:50,background:P.bg}}>✕</button>
                </div>
              </div>

              {/* Messages */}
              <div style={{flex:1,overflowY:"auto",padding:"16px 20px",display:"flex",flexDirection:"column",gap:12,background:P.bg}}>
                {aiMsgs.map((m,i)=>(
                  <div key={i} style={{display:"flex",flexDirection:"column",alignItems:m.role==="user"?"flex-end":"flex-start",gap:8}}>
                    <div className={m.role==="user"?"aib-user":"aib-ai"}>
                      <p style={{fontSize:14,lineHeight:1.7,color:m.role==="user"?"#fff":P.text1,whiteSpace:"pre-wrap",fontFamily:sf}}>{m.content}</p>
                    </div>
                    {m.items?.length>0&&(
                      <div style={{maxWidth:"88%",alignSelf:"flex-start"}}>
                        <p style={{fontSize:10,color:P.text3,fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",marginBottom:8,fontFamily:sf}}>Add to Oh Right</p>
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          {m.items.map((item,j)=>{
                            const isT=item.type==="task";
                            return(
                              <div key={j} className="ai-item">
                                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                                  <div style={{flex:1}}>
                                    {isT&&<div style={{marginBottom:6}}><PBadge pid={item.priority||"medium"}/></div>}
                                    {!isT&&<div style={{marginBottom:6}}><CatBadge cid={item.type==="quote"?"quote":item.type==="affirmation"?"affirmation":"advice"}/></div>}
                                    <p style={{fontSize:13,fontStyle:"italic",lineHeight:1.5,color:P.text1,fontFamily:ff}}>{item.text}</p>
                                    {item.author&&<p style={{fontSize:11,color:P.text3,marginTop:3,fontFamily:sf}}>— {item.author}</p>}
                                    {isT&&<p style={{fontSize:11,color:P.text3,marginTop:3,fontFamily:sf}}>{S(item.timeSlot||"anytime").emoji} {S(item.timeSlot||"anytime").label}</p>}
                                  </div>
                                  <button onClick={()=>addAiItem(item)} style={{background:P.accent,color:"#fff",border:"none",borderRadius:10,padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0,fontFamily:sf,whiteSpace:"nowrap"}}>+ Add</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {aiLoad&&(
                  <div className="aib-ai">
                    <div style={{display:"flex",gap:5,alignItems:"center",padding:"2px 0"}}>
                      {[0,1,2].map(i=><div key={i} style={{width:8,height:8,borderRadius:50,background:P.border2,animation:`tpulse 1.2s ${i*.2}s ease-in-out infinite`}}/>)}
                    </div>
                  </div>
                )}
                <div ref={aiEnd}/>
              </div>

              {/* Input */}
              <div style={{padding:"12px 16px 28px",background:P.card,borderTop:`1px solid ${P.border}`,display:"flex",gap:10,alignItems:"flex-end",flexShrink:0}}>
                <textarea style={{flex:1,padding:"12px 16px",background:P.bg,border:`1.5px solid ${P.border}`,borderRadius:24,fontSize:14,fontFamily:sf,color:P.text1,resize:"none",maxHeight:100,outline:"none",transition:"border-color .15s",lineHeight:1.5}} rows={1} placeholder="Dump your thoughts, plan your day…" value={aiIn} onChange={e=>setAiIn(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendAi();}}} onFocus={e=>e.target.style.borderColor=P.accent} onBlur={e=>e.target.style.borderColor=P.border}/>
                <button onClick={sendAi} disabled={aiLoad||!aiIn.trim()} style={{width:42,height:42,borderRadius:50,background:aiLoad||!aiIn.trim()?P.border:P.accent,color:"#fff",border:"none",cursor:aiLoad||!aiIn.trim()?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0,transition:"all .15s"}}>→</button>
              </div>
            </div>
          </>
        )}

      </>}

        {/* ════ FOCUS SETTINGS SHEET ═════════════════════════════════════ */}
        {fSettingsOpen&&(
          <>
            <div className="overlay" onClick={()=>setFSettingsOpen(false)}/>
            <div className="sheet">
              <div className="handle"/>
              <div className="sheet-hdr">
                <h3 style={{fontSize:22,fontFamily:ff}}>Session settings</h3>
                <button onClick={()=>setFSettingsOpen(false)} style={{color:P.text3,fontSize:18}}>✕</button>
              </div>
              <div style={{marginBottom:22}}>
                <Lbl>Focus on (optional)</Lbl>
                <div className="hscroll">
                  <button onClick={()=>setFTask(null)} style={{flexShrink:0,padding:"8px 14px",borderRadius:50,fontSize:12,fontWeight:600,background:!fTask?P.text1:P.card,color:!fTask?"#fff":P.text2,border:`1.5px solid ${!fTask?P.text1:P.border}`,cursor:"pointer",fontFamily:sf,whiteSpace:"nowrap"}}>Just focus</button>
                  {tasks.map(n=>(
                    <button key={n.id} onClick={()=>setFTask(n.id)} style={{flexShrink:0,padding:"8px 14px",borderRadius:50,fontSize:12,fontWeight:600,background:fTask===n.id?P.accent:P.card,color:fTask===n.id?"#fff":P.text2,border:`1.5px solid ${fTask===n.id?P.accent:P.border}`,cursor:"pointer",whiteSpace:"nowrap",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",fontFamily:sf}}>
                      {n.text.length>24?n.text.slice(0,24)+"…":n.text}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{marginBottom:22}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <Lbl style={{margin:0}}>Block during session</Lbl>
                  <button onClick={suggestApps} disabled={aiAL} style={{fontSize:11,color:P.accent,fontWeight:700,cursor:aiAL?"not-allowed":"pointer",background:"none",border:"none",fontFamily:sf}}>{aiAL?"✦ thinking…":"✦ AI suggest"}</button>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                  {APPS.map(app=>{const sel=fApps.includes(app);return(
                    <button key={app} onClick={()=>setFApps(s=>sel?s.filter(a=>a!==app):[...s,app])} style={{padding:"7px 13px",borderRadius:50,fontSize:12,fontWeight:600,background:sel?P.peach:P.card,border:`1.5px solid ${sel?P.amber:P.border}`,color:sel?P.coral:P.text2,cursor:"pointer",transition:"all .15s",fontFamily:sf}}>{sel?"🔒 ":""}{app}</button>
                  );})}
                </div>
              </div>
              <button className="btn btn-primary" onClick={()=>setFSettingsOpen(false)}>Done</button>
            </div>
          </>
        )}

        {/* Toast */}
      {toast&&<div style={{position:"fixed",bottom:90,left:"50%",transform:"translateX(-50%)",padding:"10px 22px",borderRadius:50,fontFamily:sf,fontSize:13,fontWeight:700,zIndex:999,whiteSpace:"nowrap",color:"#fff",boxShadow:"0 4px 16px rgba(0,0,0,.14)",background:toast.col,animation:"fu .2s ease"}}>{toast.msg}</div>}

    </div>
    </div>
  );
}
